// ============================================================================
// OSINT Tab — Subject Profiling integration (Feature 003)
// ============================================================================
// Operator-gated 2-stage flow dengan 6 states:
//   never_run → stage_1_running → awaiting_selection → stage_2_running → complete
//   atau → failed (dengan retry option)
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Loader2, Check, AlertTriangle, RefreshCw, Flag,
  Globe, Music, ExternalLink, Calendar, Lock, Hash, Users,
  Video, MessageCircle, Newspaper, Scale, Building2,
  Download, FileText, Printer, Trash2,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import {
  discoverCandidates, enrichCandidates, getRunStatus, getCandidates,
  getFindings, getSummary, flagFinding, purgeSubject, FEATURE_FLAGS,
} from '../lib/osint';
import { isRealDataSubject, isApifyLive } from '../lib/osint/realDataGate';
import { handleImgFail } from '../lib/imgFallback';
import { pingHFSpace } from '../lib/aiScanner';
import * as Sentry from '@sentry/react';
import { exportWordReport, exportPdfReport, checkReportQuota } from '../lib/osint/reportExport';
import { getAllPlatformsHealth, statusBadge } from '../lib/osint/actorHealth';
import { computeCompositeScore, buildCrossPlatformContext } from '../lib/osint/compositeScore';
import { buildAuditTrail, downloadAudit } from '../lib/osint/auditExport';
import { checkBiometricGateFromState } from '../utils/biometricGate';
import { CandidateScoreBreakdown } from './osint/CandidateScoreBreakdown';
import {
  recordShadowEvent, finalizeShadow, hasShadow, replayShadow,
} from '../lib/osint/shadowRun';
import { getAuthHeaders } from '../lib/auth';
import PhotoMatchExplanation from './osint/PhotoMatchExplanation';
import { POSE_LABELS } from './PhotoGallery';
import { supabase } from '../lib/supabase';
// SoulSearch Phase B — integrasi ke OsintTab per Feature 003 § 4.5
// Tidak ada halaman baru — SoulSearch adalah upgrade cinematic dari OsintTab
import ThreatScoreGauge  from './soulsearch/ThreatScoreGauge';
import BreachCheckBadge  from './soulsearch/BreachCheckBadge';
import NetworkGraph      from './soulsearch/NetworkGraph';

// § 4.4b granular failure messages — translate raw error ke pesan operator-friendly
function translateError(rawError, lang) {
  if (!rawError) return lang === 'id' ? 'Kesalahan tidak diketahui.' : 'Unknown error.';
  const e = String(rawError).toLowerCase();
  const M = {
    id: {
      no_profiles:    'Tidak ada profil publik ditemukan untuk subjek ini di platform yang dicari.',
      stage1_failed:  'Pencarian gagal — coba lagi nanti.',
      stage2_partial: 'Beberapa platform tidak tersedia. Hasil dari platform lain ditampilkan.',
      all_failed:     'Layanan Digital Forensic sementara tidak tersedia. Coba lagi nanti.',
      concurrent:     'Digital Forensic sedang berjalan untuk subjek ini. Tunggu hingga selesai.',
      photo_expired:  'Beberapa URL foto tidak dapat diakses. Skor kecocokan foto mungkin tidak lengkap.',
      budget:         'Kuota Digital Forensic bulanan telah tercapai. Hubungi admin.',
      timeout:        'Pencarian melebihi batas waktu. Coba lagi.',
      auth:           'Token akses tidak valid. Hubungi admin untuk konfigurasi ulang.',
    },
    en: {
      no_profiles:    'No public profiles found for this subject across the searched platforms.',
      stage1_failed:  'Discovery failed — please try again later.',
      stage2_partial: 'Some platforms unavailable. Results from other platforms shown.',
      all_failed:     'Digital Forensic service temporarily unavailable. Please try again later.',
      concurrent:     'Digital Forensic is already running for this subject. Please wait.',
      photo_expired:  'Some photo URLs could not be accessed. Photo match score may be incomplete.',
      budget:         'Monthly Digital Forensic quota reached. Contact admin.',
      timeout:        'Search exceeded time limit. Please try again.',
      auth:           'Access token invalid. Contact admin to reconfigure.',
    },
  }[lang === 'id' ? 'id' : 'en'];
  if (e.includes('no candidate') || e.includes('no profile')) return M.no_profiles;
  if (e.includes('budget') || e.includes('quota'))             return M.budget;
  if (e.includes('timeout') || e.includes('aborted'))          return M.timeout;
  if (e.includes('token') || e.includes('401') || e.includes('403')) return M.auth;
  if (e.includes('photo') && e.includes('expired'))            return M.photo_expired;
  if (e.includes('partial') || e.includes('some platform'))    return M.stage2_partial;
  if (e.includes('already running') || e.includes('concurrent'))return M.concurrent;
  if (e.includes('all') && e.includes('failed'))               return M.all_failed;
  return rawError; // fallback ke raw error
}

const PLATFORM_ICON = {
  facebook:  Users,
  instagram: Hash,
  tiktok:    Music,
  threads:   MessageCircle,
  x:         Hash,
  twitter:   Hash,
  youtube:   Video,
  linkedin:  Users,
  web:       Globe,
  news:      Newspaper,
  court:     Scale,
  gov:       Building2,
  // Maigret-discovered platforms (Phase 4 spec)
  github:    Hash,
  reddit:    MessageCircle,
  pinterest: Hash,
  medium:    FileText,
  tumblr:    Hash,
  mastodon:  MessageCircle,
  telegram:  MessageCircle,
};

const PLATFORM_LABEL = {
  // 4-RADAR discovery engines (Layer 1)
  radar_a:  'NETCOMM RADAR-A',
  radar_b:  'NETCOMM RADAR-B',
  radar_c:  'NETCOMM RADAR-C',
  radar_d:  'NETCOMM RADAR-D',
  // Target platforms (Layer 2 scraping)
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok',
  threads:  'Threads',
  x:        'X (Twitter)', twitter: 'X (Twitter)',
  youtube:  'YouTube',
  linkedin: 'LinkedIn',
  web:      'Google',
  news:     'Berita',
  court:    'Pengadilan',
  gov:      'Pemerintah',
  // Maigret-discovered platforms
  github:    'GitHub',
  reddit:    'Reddit',
  pinterest: 'Pinterest',
  medium:    'Medium',
  tumblr:    'Tumblr',
  mastodon:  'Mastodon',
  telegram:  'Telegram',
};

// Sprint 007 source_attribution_label_inventory — UPPERCASE branded labels
// untuk Intelligence Findings column + B-002 forensic report footnotes.
const SOURCE_BRAND_LABEL = {
  facebook:  'FACEBOOK',
  instagram: 'INSTAGRAM',
  linkedin:  'LINKEDIN',
  tiktok:    'TIKTOK',
  threads:   'THREADS',
  x:         'X / TWITTER',
  twitter:   'X / TWITTER',
  youtube:   'YOUTUBE',
  web:       'WEB SEARCH',
  news:      'NEWS / DETIK.COM',
  court:     'MA SCRAPER',
  gov:       'GOV REGISTRY',
  github:    'GITHUB',
  reddit:    'REDDIT',
  pinterest: 'PINTEREST',
  medium:    'MEDIUM',
  tumblr:    'TUMBLR',
  mastodon:  'MASTODON',
  telegram:  'TELEGRAM',
  bio:       'BIO PARSE',
  cross:     'CROSS-REF',
  hibp:      'HIBP CHECK',
};

// Stage1ElapsedTimer — MM:SS counter for theater header.
// Mounts when theater visible, ticks every second sampai isComplete=true.
// Saat complete: interval cleared, label switch ELAPSED → DURATION
// (theater tetap mounted sampai operator klik LANJUT KE STAGE 2, jadi timer
// harus berhenti sendiri di phase=complete supaya gak terus jalan).
function Stage1ElapsedTimer({ isComplete = false }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (isComplete) return; // stop accumulating begitu pipeline complete
    const start = Date.now() - elapsed * 1000; // resume dari elapsed terakhir
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return <>{isComplete ? 'DURATION' : 'ELAPSED'} {mm}:{ss}</>;
}

// Confidence band → warna + label
const confidenceBand = (conf, lang) => {
  if (conf >= 0.85) return { color: 'var(--color-green)', label: lang === 'id' ? 'Match Tinggi' : 'High match' };
  if (conf >= 0.65) return { color: 'var(--color-yellow)',           label: lang === 'id' ? 'Kemungkinan'   : 'Likely'     };
  if (conf >= 0.50) return { color: 'var(--color-orange)',           label: lang === 'id' ? 'Mungkin'       : 'Possible'   };
  return                  { color: 'var(--text-mute)',   label: lang === 'id' ? 'Lemah'         : 'Weak'       };
};

// § 4.4 item 15: configurable display threshold, default hide < 50%
// Display threshold — lebih permisif untuk kasus low-public-footprint subject.
// 0.50 → 0.30: kandidat lemah tetap visible supaya operator review manual.
// Operator masih bisa filter via toggle "Hide weak matches" kalau mau.
const DISPLAY_THRESHOLD = 0.30;

const OsintTab = ({ subject, operatorId = 'demo-operator', autoStart = false, appHfStatus = 'online' }) => {
  const { lang } = useApp();

  // Sentry context tags — supaya error di dashboard ke-tag dgn operator + subject
  // + stage. Memudahkan filter "show errors for ANDRA SANDI" atau "errors di Stage 2".
  // Tags update saat subject/operator change.
  useEffect(() => {
    Sentry.setTag('operator_id', operatorId);
    if (subject?.nik) Sentry.setTag('subject_nik', subject.nik);
    if (subject?.nama_lengkap) Sentry.setContext('subject', {
      nik: subject.nik,
      nama_lengkap: subject.nama_lengkap,
      kota: subject.kota_kabupaten,
    });
  }, [operatorId, subject?.nik, subject?.nama_lengkap, subject?.kota_kabupaten]);

  const [state, setState]           = useState('never_run');

  // Sentry stage tag — update saat OSINT pipeline transition. Operator activity
  // context: stage1 running → awaiting selection → stage2 enrichment → complete.
  useEffect(() => {
    Sentry.setTag('osint_stage', state);
  }, [state]);
  const [showWeakMatches, setShowWeakMatches] = useState(false);
  // states: never_run | stage_1_running | awaiting_selection | stage_2_running | complete | failed
  const [runId, setRunId]           = useState(null);
  const [runStartedAt, setRunStartedAt] = useState(null);  // ISO timestamp Stage 1 start untuk audit export
  const [usernameHint, setUsernameHint] = useState('');
  // GAP-2: Optional inputs city + employer (boost Stage 1 search accuracy)
  const [cityHint, setCityHint]         = useState('');
  const [employerHint, setEmployerHint] = useState('');
  // Spec 2026-05-09: keywords[] max 5 — kata/kalimat untuk dorking Q5 + bonus scoring
  const [keywords, setKeywords]         = useState([]);  // string[]
  const [keywordDraft, setKeywordDraft] = useState('');
  // Platform selection — full sosmed coverage default per CSOO directive (2026-05-09):
  // IG, FB, LinkedIn, TikTok, X, Threads, YouTube, Google (web). News + Maps stay
  // opt-in (lower per-run signal density).
  const DEFAULT_PLATFORMS = ['instagram','facebook','linkedin','tiktok','x','threads','youtube','web'];
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set(DEFAULT_PLATFORMS));
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // GAP-7: Phone added as separate elevated data type
  const [dataTypes, setDataTypes]   = useState(new Set(['photos', 'posts', 'mentions', 'public_records', 'location', 'phone']));
  const [findings, setFindings]     = useState([]);
  const [summary, setSummary]       = useState(null);
  const [error, setError]           = useState(null);
  // G-005 actor health (per-platform status untuk UI indicator)
  const [healthMap, setHealthMap]   = useState(new Map());

  // ─── Phase 1+2+3 (spec 22.0) — Cinematic streaming UX ──────────────────────
  const [hfStatus, setHfStatus]               = useState('unknown'); // unknown|warming|ready|cold|sleeping
  const [streamMode, setStreamMode]           = useState(false);
  const [pipelinePhase, setPipelinePhase]     = useState('idle');    // idle|warming|stage1|discovered|stage2|face|complete|error
  const [platformStates, setPlatformStates]   = useState({});        // { platform: { phase, count, items? } }
  const [liveCount, setLiveCount]             = useState(0);
  const [streamSource, setStreamSource]       = useState(null);      // 'live' | 'replay' | null

  // Face match streaming live di Stage 1 — biometric anchor + per-candidate scan
  const [faceMatchSubject, setFaceMatchSubject] = useState(null);  // { ready, dim, foto_url }
  const [faceMatchStream, setFaceMatchStream]   = useState([]);    // [{ id, platform, handle, photo_url, status, similarity, tier }]

  // Reverse Image Search (Yandex) — alias detection layer
  // status: idle | running | done | failed
  const [risStatus, setRisStatus] = useState({ status: 'idle', additional_candidates: 0, total_hits: 0 });
  const [imageSerpStats, setImageSerpStats] = useState({ scanned: 0, total: 0, matched: 0 });
  // Identity Lock flash — one-shot 2.4s celebratory overlay saat subject_ready
  // transition idle → ready. Reset di startDiscovery, re-fire saat NIK diganti.
  const [identityLockFlash, setIdentityLockFlash] = useState(false);

  // Spec 2026-05-09: LOW tier collapsed by default — operator klik expand
  const [lowExpanded, setLowExpanded] = useState(false);

  // Google Docs Section 4.2: Active breakdown popover untuk kandidat yang di-hover
  const [breakdownEntry, setBreakdownEntry] = useState(null);  // {entry, x, y} | null

  // Stage 1 → Stage 2 transition gate. Sebelumnya theater auto-unmount saat
  // pipeline.complete → awaiting_selection muncul tanpa konfirmasi operator.
  // Sekarang: theater stay frozen di state final (semua match terlihat), operator
  // baca dulu, lalu klik "LANJUT" untuk advance ke selection screen.
  const [theaterDismissed, setTheaterDismissed] = useState(false);

  // SoulSearch Phase B — state integrasi (per Feature 003 § 4.5 result display)
  const [threatData, setThreatData]     = useState(null);   // { threat_score, category, factors, narrative }
  const [breachData, setBreachData]     = useState([]);     // array breach findings
  const [networkEdges, setNetworkEdges] = useState([]);     // edges untuk NetworkGraph
  const [showSoulSearch, setShowSoulSearch] = useState(true); // auto-show after Stage 3

  // Stage 3 / Decision 2 — operator filter findings sebelum threat assessment
  const [filterSelections, setFilterSelections] = useState(new Set()); // finding IDs yang masuk Stage 3
  const [stage3Error, setStage3Error]           = useState(null);

  // B-002 Forensic Analysis Report Subsystem state
  const [showForensicModal, setShowForensicModal] = useState(false);
  const [forensicReportType, setForensicReportType] = useState('analytical'); // 'factual' | 'analytical'
  const [forensicClassification, setForensicClassification] = useState('INTERNAL');
  // B-002 acceptance #3: operator selects which photos masuk laporan.
  // Default: include all enrolled subject photos + all face match photos.
  // Set of URL string. Limit 12 untuk file size control.
  const [forensicSelectedPhotos, setForensicSelectedPhotos] = useState(new Set());
  const [forensicGenerating, setForensicGenerating] = useState(false);
  const [forensicResult, setForensicResult] = useState(null);
  const [forensicError, setForensicError] = useState(null);

  // G-003 async-safe: persist runId per subject di sessionStorage supaya
  // operator bisa navigate away → run continues server-side → resume saat balik.
  const RUN_KEY = subject?.nik ? `horus_osint_run_${subject.nik}` : null;

  // Phase 1: Anticipatory HF warmup saat operator buka subjek (debounced 500ms).
  // hf-health endpoint hanya forward {ok:true} dari HF Space; klasifikasi
  // ready/warming/cold/sleeping di-derive client-side dari elapsed response time.
  useEffect(() => {
    if (!subject?.nik) return;
    setHfStatus('warming');
    const t = setTimeout(async () => {
      const t0 = Date.now();
      try {
        const r = await fetch('/api/hf-health');
        const elapsed = Date.now() - t0;
        if (!r.ok) {
          setHfStatus(r.status === 502 ? 'sleeping' : 'unknown');
          return;
        }
        // Klasifikasi berdasar elapsed (mirror lama hf-warmup logic)
        setHfStatus(
          elapsed > 5000 ? 'cold'
        : elapsed > 1000 ? 'warming'
        : 'ready'
        );
      } catch {
        setHfStatus('unknown');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [subject?.nik]);

  // Load existing data on mount + G-003 resume in-progress run + G-005 health
  useEffect(() => {
    if (!subject?.nik) return;

    // G-003 resume: kalau sessionStorage punya runId untuk subject ini, fetch status
    let resumeRunId = null;
    try { resumeRunId = sessionStorage.getItem(RUN_KEY); } catch {}

    Promise.all([
      getSummary(subject.nik),
      getFindings(subject.nik, false, operatorId),
      // G-005: Fetch health untuk 7 platform default
      getAllPlatformsHealth(['facebook','instagram','tiktok','threads','x','youtube','linkedin']),
      // G-003: kalau ada resume runId, fetch latest run state
      resumeRunId ? getRunStatus(resumeRunId) : Promise.resolve(null),
    ]).then(([sum, fnd, health, runStatus]) => {
      setSummary(sum);
      setHealthMap(health);
      // G-003: kalau run masih running, restore state
      if (runStatus?.run && ['stage_1_running', 'stage_2_running', 'awaiting_selection'].includes(runStatus.run.stage)) {
        setRunId(runStatus.run.id);
        setState(runStatus.run.stage);
        // Re-fetch candidates kalau awaiting_selection
        if (runStatus.run.stage === 'awaiting_selection') {
          getCandidates(runStatus.run.id).then(r => setCandidates(r.candidates || []));
        }
      } else if (resumeRunId) {
        // Run completed — clear sessionStorage
        try { sessionStorage.removeItem(RUN_KEY); } catch {}
      }
      if (fnd.findings?.length > 0) {
        setFindings(fnd.findings);
        setState('complete');
      }
    });
  }, [subject?.nik]);

  // Stage 1 — Discovery
  const handleDiscover = async (force = false) => {
    if (!subject?.nik) return;

    // § 6 Edge: Concurrent run prevention
    if (state === 'stage_1_running' || state === 'stage_2_running') {
      console.warn('[osint] run already in progress, ignoring click');
      return;
    }

    // § 4.1 Iterative: Warning kalau run terakhir < 5 menit
    if (!force && summary?.last_run_at) {
      const elapsed = Date.now() - new Date(summary.last_run_at).getTime();
      const minutes = Math.floor(elapsed / 60000);
      if (minutes < 5) {
        const ok = window.confirm(
          lang === 'id'
            ? `Run terakhir ${minutes} menit lalu. Re-run akan menambah biaya API. Lanjut?`
            : `Last run was ${minutes} minutes ago. Re-running will add API cost. Continue?`
        );
        if (!ok) return;
      }
    }

    setError(null);
    setState('stage_1_running');
    setCandidates([]);
    setSelectedIds(new Set());

    // GAP-1+2: Pass selected platforms + city/employer hints supaya Stage 1
    // hemat budget (skip platform tidak dipilih) + akurasi naik (city/employer
    // boost Google Search query)
    const platforms = Array.from(selectedPlatforms);
    if (platforms.length === 0) {
      setError(lang === 'id' ? 'Pilih minimal 1 platform untuk search.' : 'Select at least 1 platform to search.');
      setState('never_run');
      return;
    }
    const result = await discoverCandidates(
      {
        ...subject,
        username: usernameHint || null,
        city:     cityHint || null,
        employer: employerHint || null,
        keywords: keywords.length > 0 ? keywords : null,
      },
      operatorId,
      platforms
    );

    if (result.error) {
      setError(result.error);
      setState('failed');
      return;
    }

    setRunId(result.run_id);
    // G-003 async-safe: persist runId di sessionStorage
    if (RUN_KEY) try { sessionStorage.setItem(RUN_KEY, result.run_id); } catch {}
    setCandidates(result.candidates || []);

    // GAP-FIX: Run kosong (0 kandidat baru) tapi punya cached findings →
    // tampilkan cache supaya operator tidak lihat "0 results" padahal data ada.
    if ((result.candidates || []).length === 0 && findings.length > 0) {
      console.log('[OSINT] Stage 1 returned 0 new candidates, falling back to cached findings:', findings.length);
      setState('complete');
      if (RUN_KEY) try { sessionStorage.removeItem(RUN_KEY); } catch {}
    } else {
      setState('awaiting_selection');
      // Auto-check kandidat dengan confidence >= 0.85 (HIGH match)
      // Operator masih bisa uncheck atau add manual.
      const autoConfirm = (result.candidates || [])
        .filter(c => (c.confidence || 0) >= 0.85)
        .map(c => c.id);
      if (autoConfirm.length > 0) setSelectedIds(new Set(autoConfirm));
    }

    // GAP-FIX: Refresh healthMap setelah Stage 1 complete supaya UI badge
    // update dari UNKNOWN → HEALTHY/DEGRADED/FAILING per actual actor result.
    // Wait 2s supaya audit log writes selesai propagate ke DB.
    setTimeout(async () => {
      try {
        const fresh = await getAllPlatformsHealth(['facebook','instagram','tiktok','threads','x','youtube','linkedin','web','news']);
        setHealthMap(fresh);
      } catch (err) {
        console.warn('[OsintTab] health refresh fail:', err.message);
      }
    }, 2000);

    // Diagnostic console log (visible di F12 untuk debug demo)
    console.log('[OSINT Stage 1] Result:', {
      run_id: result.run_id,
      candidates_total: result.candidates?.length || 0,
      candidates_above_threshold: (result.candidates || []).filter(c => c.confidence >= DISPLAY_THRESHOLD).length,
      platforms_dispatched: platforms,
      subject_has_photo: !!subject.foto_url,
      username_hint: usernameHint || null,
    });
  };

  // ─── Phase 2 — SSE Streaming Pipeline (spec 22.0) ──────────────────────────
  // Cinematic visual layer; existing handleDiscover dipanggil parallel untuk
  // menjamin data persistence via path teruji. Jika SSE drop di Hobby plan
  // (60s cap), shadow replay jadi safety net.
  const handleSSEEvent = useCallback((type, data) => {
    switch (type) {
      case 'pipeline.start':
        setPipelinePhase('stage1');
        setPlatformStates({});
        setLiveCount(0);
        break;
      case 'platform.start':
        setPlatformStates(s => ({ ...s, [data.platform]: { phase: 'running', count: 0 } }));
        break;
      case 'platform.progress':
        setPlatformStates(s => ({
          ...s,
          [data.platform]: { ...(s[data.platform] || {}), phase: 'partial', count: data.count },
        }));
        setLiveCount(c => c + 1);
        break;
      case 'platform.done':
        setPlatformStates(s => ({ ...s, [data.platform]: { phase: 'done', count: data.count, items: data.items } }));
        setLiveCount(c => c + (data.count || 0));
        break;
      case 'platform.timeout':
        setPlatformStates(s => ({ ...s, [data.platform]: { phase: 'timeout', count: data.partial || 0, reason: data.reason } }));
        break;
      case 'platform.skip':
        setPlatformStates(s => ({ ...s, [data.platform]: { phase: 'skip', count: 0, reason: data.reason } }));
        break;
      case 'platform.error':
        // Capture detailed error reason untuk operator visibility
        setPlatformStates(s => ({ ...s, [data.platform]: {
          phase: 'error', count: 0,
          reason: data.message || 'unknown_error',
          detail: data.detail,
        } }));
        break;
      case 'pipeline.discovered':
        setPipelinePhase('stage2');
        break;
      case 'stage.face_match':
        setPipelinePhase('face');
        break;
      case 'face_match.subject_ready':
        setFaceMatchSubject(prev => {
          const wasReady = !!prev?.ready;
          const nowReady = !!data.ready;
          // Identity Lock animation trigger: transition idle → ready
          // Fire one-shot flash overlay (2.4s) only ONCE per session.
          if (nowReady && !wasReady) {
            setIdentityLockFlash(true);
            setTimeout(() => setIdentityLockFlash(false), 2600);  // match keyframe duration
          }
          return {
            ready: nowReady,
            dim: data.dim || 0,
            foto_url: data.foto_url,
            embedding_count: data.embedding_count || 0,
            all_photos: Array.isArray(data.all_photos) && data.all_photos.length > 0
              ? data.all_photos
              : [data.foto_url].filter(Boolean),
          };
        });
        break;
      case 'face_match.error':
        // Subject biometric tidak available — log warning, lanjut text-only
        console.warn('[face_match]', data.message || data.reason);
        setFaceMatchSubject(prev => ({ ...(prev || {}), ready: false, error: data.reason }));
        break;
      case 'face_match.start': {
        // Idempotent: kalau entry sudah ada (dari bridge text-only atau prior start),
        // upgrade status ke 'scanning' alih-alih push duplicate. Mencegah dupe entries
        // ketika handleDiscover bridge run sebelum SSE face_match.start.
        const id = `${data.platform}:${data.handle || data.photo_url}`;
        setFaceMatchStream(s => {
          const existing = s.find(e => e.id === id);
          if (existing) {
            return s.map(e => e.id === id ? {
              ...e,
              status:       'scanning',
              total_photos: data.total_photos || 1,
              deep_scan:    !!data.deep_scan,
              scanned:      0,
              text_only:    false,  // SSE picked it up — bukan text-only lagi
            } : e);
          }
          return [...s, {
            id,
            platform: data.platform, handle: data.handle, photo_url: data.photo_url,
            status: 'scanning', similarity: null, tier: null,
            total_photos: data.total_photos || 1,
            deep_scan: !!data.deep_scan,
            scanned: 0,
          }];
        });
        break;
      }
      case 'face_match.progress': {
        // Deep scan progress per kandidat (multi-photo). Accumulate scanned
        // photos dgn similarity untuk cinematic photo grid render.
        const id = `${data.platform}:${data.handle}`;
        setFaceMatchStream(s => s.map(e => {
          if (e.id !== id) return e;
          const scannedPhotos = Array.isArray(e.scanned_photos) ? e.scanned_photos : [];
          // Update existing (same URL) atau append
          let next = scannedPhotos;
          if (data.photo_url) {
            const exists = scannedPhotos.findIndex(p => p.url === data.photo_url);
            const entry = {
              url: data.photo_url,
              similarity: data.latest_similarity || 0,
              no_face: !!data.no_face,
            };
            next = exists >= 0
              ? scannedPhotos.map((p, i) => i === exists ? entry : p)
              : [...scannedPhotos, entry];
          }
          return {
            ...e,
            scanned: data.scanned,
            total_photos: data.total_photos || data.total || e.total_photos,
            scanned_photos: next,
          };
        }));
        break;
      }
      case 'face_match.result': {
        const id = `${data.platform}:${data.handle || data.photo_url}`;
        setFaceMatchStream(s => s.map(e =>
          e.id === id ? {
            ...e, status: 'done',
            similarity: data.similarity, tier: data.tier,
            photo_url: data.photo_url || e.photo_url,
            avatar_url: data.avatar_url || e.avatar_url,
            photos_scanned: data.photos_scanned,
            photos_matching: data.photos_matching,
            photos_with_face: data.photos_with_face,
            all_scores: Array.isArray(data.all_scores) ? data.all_scores : e.all_scores,
            disguised: !!data.disguised,
            display_name: data.display_name,
            similarity_max: data.similarity_max ?? null,
            similarity_mean_top3: data.similarity_mean_top3 ?? null,
            subject_poses_used: data.subject_poses_used ?? null,
            // V-002 per-signal breakdown for INTELLIGENCE FINDINGS render
            name_match:     data.name_match ?? null,
            username_match: data.username_match ?? null,
            photo_match:    data.photo_match ?? data.similarity,
            auto_confirm:   !!data.auto_confirm,
          } : e
        ));
        break;
      }
      case 'photo_first.start':
        setRisStatus({ status: 'running', additional_candidates: 0, total_hits: 0 });
        break;
      case 'photo_first.done':
        setRisStatus({
          status: data.reason === 'error' ? 'failed' : 'done',
          additional_candidates: data.additional_candidates || 0,
          total_hits: data.total_yandex_hits || 0,
          reason: data.reason,
        });
        break;
      case 'image_serp.progress':
        // RADAR-D image SERP scan progress — accumulate scanned count untuk
        // header counter "X foto SERP dipindai"
        setImageSerpStats(s => ({
          scanned: data.scanned || (s?.scanned || 0) + 1,
          total: data.total || s?.total || 0,
          matched: s?.matched || 0,
        }));
        break;
      case 'image_serp.match':
        // Foto match dari image SERP — tambah sebagai entry di faceMatchStream
        // dgn source `image_serp_<engine>` supaya muncul di Intelligence Findings
        setImageSerpStats(s => ({
          scanned: s?.scanned || 0,
          total: s?.total || 0,
          matched: (s?.matched || 0) + 1,
        }));
        setFaceMatchStream(s => {
          const id = `image_serp:${data.image_url}`;
          if (s.find(e => e.id === id)) return s;
          return [...s, {
            id,
            platform: 'image_serp',
            handle: null,
            display_name: data.title || data.source_url || 'Image SERP match',
            source_url: data.source_url,
            photo_url: data.image_url,
            status: 'done',
            similarity: data.similarity,
            tier: data.tier,
            photo_match: data.similarity,
            engine: data.engine,
            source: `radar_d_${data.engine || 'image_serp'}`,
            scanned_photos: [{ url: data.image_url, similarity: data.similarity }],
            total_photos: 1,
          }];
        });
        break;
      case 'pipeline.complete':
        setPipelinePhase('complete');
        break;
      case 'pipeline.error':
        setPipelinePhase('error');
        setError(data.message || 'pipeline error');
        break;
      default: break;
    }
  }, []);

  // SSE stream lifecycle refs:
  // - sseAbortRef: AbortController untuk fetch + reader cancel (cleanup unmount,
  //   abort double-run, prevent memory leak EventSource hang).
  // - isStreamingRef: mutex guard supaya operator double-click discover gak
  //   trigger 2x Apify cost. Locked saat runOsintStream mulai, released saat
  //   kedua child (SSE + handleDiscover) resolve atau abort.
  const sseAbortRef    = useRef(null);
  const isStreamingRef = useRef(false);
  // Stage 2 enrichment mutex — handleEnrich + handleEnrichMore hit Apify via
  // enrichCandidates → /api/apify-enrich. Double-click = duplicate enrichment
  // run = 2× cost untuk hasil sama. Pattern sama dgn isStreamingRef Stage 1.
  // Forensic report Stage 3 sudah ada state-based guard `forensicGenerating`
  // (line 815), tidak perlu ref tambahan.
  const isEnrichingRef = useRef(false);

  // Cleanup on unmount — abort any in-flight SSE stream supaya reader gak hang
  // di server (Vercel function tetap jalan sampai timeout 60s/300s kalau klien
  // disconnect tanpa abort).
  useEffect(() => {
    return () => {
      if (sseAbortRef.current) {
        try { sseAbortRef.current.abort(); } catch { /* noop */ }
        sseAbortRef.current = null;
      }
    };
  }, []);

  // Bridge: handleDiscover candidates (/api/apify-discover) → faceMatchStream
  // supaya theater UI (INTELLIGENCE FINDINGS + HIGH/MED/LOW buckets) tetap
  // render walaupun SSE face_match path 0 hits.
  //
  // KRITIS untuk akurasi ALIAS detection: handleDiscover SUDAH run /api/face-compare
  // per kandidat via scoreCandidate(), hasil tersimpan di match_signals.photo_match
  // (biometric cosine 0-1). Pull signal ini → compositeScore biometric tier
  // bekerja → kandidat dgn wajah cocok TAPI nama berbeda (alias) ter-flag via
  // aliasDetected (photo≥0.65 ∧ name<0.30) → muncul di HIGH/MED bukan terkubur.
  //
  // Kasus nyata 2026-05-19: ANDRA SANDI run 6b9a08ab — 28 kandidat, 10 above
  // threshold; sebelumnya tidak ter-render karena similarity dibuang ke null.
  useEffect(() => {
    if (!candidates || candidates.length === 0) return;
    setFaceMatchStream(prev => {
      const existingIds = new Set(prev.map(e => e.id));
      const additions = [];
      for (const c of candidates) {
        const handle   = c.profile_handle || c.handle || c.username;
        const photoUrl = c.thumbnail_url  || c.photo_url;
        const profileUrl = c.profile_url || c.url;
        const key = `${c.platform}:${handle || photoUrl || profileUrl}`;
        if (!key || key.endsWith(':') || existingIds.has(key)) continue;
        existingIds.add(key);

        // Pull biometric + name signals dari scoreCandidate output.
        const ms = c.match_signals || {};
        const photoMatch = (typeof ms.photo_match    === 'number') ? ms.photo_match    : null;
        const nameMatch  = (typeof ms.name_match     === 'number') ? ms.name_match     : null;
        const userMatch  = (typeof ms.username_match === 'number') ? ms.username_match : null;

        // Derive tier dari biometric similarity — selaras dgn SSE face_match.result
        // ladder (strong ≥0.80, match ≥0.60, possible ≥0.45, unlikely >0, failed 0).
        let tier = null;
        if (photoMatch != null) {
          tier = photoMatch >= 0.80 ? 'strong'
               : photoMatch >= 0.60 ? 'match'
               : photoMatch >= 0.45 ? 'possible'
               : photoMatch >  0    ? 'unlikely'
               :                      'failed';
        }

        // ALIAS detection — wajah cocok kuat TAPI nama beda jauh. Penting untuk
        // kasus subjek yang punya akun pakai username/displayName palsu.
        const disguised = (photoMatch != null && nameMatch != null
                           && photoMatch >= 0.65 && nameMatch < 0.30);

        additions.push({
          id: key,
          platform:        c.platform,
          handle,
          photo_url:       photoUrl,
          status:          'done',
          similarity:      photoMatch,
          tier,
          display_name:    c.display_name,
          photo_match:     photoMatch,
          name_match:      nameMatch,
          username_match:  userMatch,
          photos_scanned:  ms.photo_total_count || 1,
          photos_matching: ms.photo_match_count || 0,
          disguised,
          text_only:       photoMatch == null,   // benar text-only kalau face-compare gagal
          confidence_text: c.confidence,
          bio:             ms.bio,
          snippet:         ms.snippet || ms.title,
        });
      }
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
  }, [candidates]);

  const runSSEStream = async () => {
    if (!subject?.nik) return;
    // Abort prior stream kalau ada (defensive — runOsintStream sudah mutex,
    // tapi defense-in-depth supaya gak ada race).
    if (sseAbortRef.current) {
      try { sseAbortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    sseAbortRef.current = controller;

    const startedAt = Date.now();
    setRunStartedAt(new Date(startedAt).toISOString());  // for audit export
    setStreamMode(true);
    setStreamSource('live');
    setPipelinePhase('warming');
    setPlatformStates({});
    setLiveCount(0);
    setFaceMatchSubject(null);
    setFaceMatchStream([]);
    setRisStatus({ status: 'idle', additional_candidates: 0, total_hits: 0 });
    setImageSerpStats({ scanned: 0, total: 0, matched: 0 });
    setIdentityLockFlash(false);
    setTheaterDismissed(false);

    const platforms = Array.from(selectedPlatforms);
    if (platforms.length === 0) {
      setError(lang === 'id' ? 'Pilih minimal 1 platform.' : 'Select at least 1 platform.');
      setStreamMode(false);
      sseAbortRef.current = null;
      return;
    }

    let reader = null;
    try {
      const res = await fetch('/api/osint-stream', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        signal:  controller.signal,
        body: JSON.stringify({
          subject: {
            nik:             subject.nik,
            nama_lengkap:    subject.nama_lengkap,
            foto_url:        subject.foto_url,
            // KTP geographic context — multi-level untuk search precision.
            // Sharpening 2026-05-19: text discovery sebelumnya cuma pakai
            // operator-filled cityHint. Sekarang KTP geo (kabupaten + kecamatan
            // + kel_desa) selalu surface sebagai context, plus provinsi via
            // lookupProvinsi di backend.
            kota_kabupaten:  subject.kota_kabupaten,
            kecamatan:       subject.kecamatan,
            kel_desa:        subject.kel_desa,
          },
          selectedPlatforms: platforms,
          dataTypes:         Array.from(dataTypes),
          usernameHint:      usernameHint || null,
          cityHint:          cityHint || null,
          employerHint:      employerHint || null,
          keywords:          keywords.length > 0 ? keywords : null,
        }),
      });
      if (!res.ok || !res.body) throw new Error('http_' + res.status);

      reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      while (true) {
        if (controller.signal.aborted) break;
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop() || '';
        for (const ev of events) {
          if (!ev.trim() || ev.startsWith(':')) continue;
          const lines = ev.split('\n');
          const evtLine = lines.find(l => l.startsWith('event:'));
          const dataLine = lines.find(l => l.startsWith('data:'));
          if (!evtLine || !dataLine) continue;
          const eventType = evtLine.slice(6).trim();
          const dataStr   = dataLine.slice(5).trim();
          try {
            const data = JSON.parse(dataStr);
            // Diagnostic console log — KRITIS untuk debug akurasi alias detection.
            // photo_first.* events = Yandex RIS path (catch alias accounts).
            // face_match.* = per-candidate biometric. platform.* = text discovery.
            // Filter noise events supaya console tidak terlalu ramai.
            if (eventType.startsWith('photo_first') || eventType.startsWith('face_match')
                || eventType === 'pipeline.start' || eventType === 'pipeline.complete'
                || eventType === 'pipeline.error'
                // Sharpening 2026-05-19: include debug.subject_load + debug.extract_error
                // untuk diagnose bg extraction yang cuma load 1 dari 5 pose subject
                || eventType.startsWith('debug.')) {
              console.log(`[SSE] ${eventType}:`, data);
            }
            handleSSEEvent(eventType, data);
            recordShadowEvent(subject.nik, eventType, data, startedAt);
          } catch (parseErr) {
            // Log parse errors instead of silent swallow — operator/dev butuh
            // visibility kalau backend kirim malformed JSON.
            console.warn('[SSE parse]', eventType, parseErr.message, dataStr.slice(0, 200));
          }
        }
      }
      finalizeShadow(subject.nik, subject);
    } catch (err) {
      // AbortError = expected (cleanup unmount atau new run), tidak perlu log error.
      if (err.name === 'AbortError') {
        console.log('[osint-stream] aborted');
      } else {
        console.warn('[osint-stream] live failed:', err.message);
        // Fallback ke shadow replay kalau ada
        if (hasShadow(subject.nik)) {
          setStreamSource('replay');
          await replayShadow(subject.nik, handleSSEEvent);
        } else {
          setPipelinePhase('error');
        }
      }
    } finally {
      // Cleanup: cancel reader + clear ref kalau ini controller yang masih aktif
      // (kalau sudah di-superseded oleh stream baru, jangan clear).
      try { if (reader) await reader.cancel().catch(() => {}); } catch { /* noop */ }
      if (sseAbortRef.current === controller) sseAbortRef.current = null;
    }
  };

  // Wrapper: dispatch SSE (visual) + handleDiscover (data persistence) parallel.
  // Spec 22.0: SSE = cinematic, /api/apify-discover = battle-tested data path.
  //
  // Mutex via isStreamingRef supaya double-click "Discover" gak fire 2x.
  // Apify cost real — duplicate runs = 2x billing untuk hasil sama.
  const runOsintStream = async () => {
    if (isStreamingRef.current) {
      console.log('[osint-stream] already running, ignoring duplicate trigger');
      return;
    }
    isStreamingRef.current = true;
    try {
      // Dual-fire — visual SSE + data discover parallel (cost: 2x Apify call OK
      // untuk demo karena cache layer sudah ada via prewarm).
      await Promise.allSettled([runSSEStream(), handleDiscover()]);
    } finally {
      isStreamingRef.current = false;
    }
  };

  // Auto-start saat dipanggil dari ForensicWorkspace dengan autoStart=true.
  // Hanya fire SEKALI saat subject pertama kali tersedia + state masih never_run.
  useEffect(() => {
    if (!autoStart || !subject?.nik) return;
    if (state !== 'never_run') return;
    const tmr = setTimeout(() => {
      runOsintStream();
    }, 600);
    return () => clearTimeout(tmr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, subject?.nik]);

  // Stage 2 — Enrichment
  const handleEnrich = async () => {
    if (!runId || selectedIds.size === 0) return;
    if (isEnrichingRef.current) {
      console.log('[enrich] already running, ignoring duplicate trigger');
      return;
    }
    isEnrichingRef.current = true;
    setError(null);
    setState('stage_2_running');

    try {
      const result = await enrichCandidates(
        runId,
        Array.from(selectedIds),
        Array.from(dataTypes),
        operatorId
      );

      if (result.error) {
        setError(result.error);
        setState('failed');
        return;
      }

      // Refetch findings + summary
      const [sum, fnd] = await Promise.all([
        getSummary(subject.nik),
        getFindings(subject.nik, false, operatorId),
      ]);
      setSummary(sum);
      const fetchedFindings = fnd.findings || [];
      setFindings(fetchedFindings);

      // Decision 2 — operator filter findings dulu sebelum Stage 3 threat assessment.
      // Default: semua finding di-include (operator bisa uncheck yang irrelevant).
      setFilterSelections(new Set(fetchedFindings.map(f => f.id || f.url)));
      setState('awaiting_filter');
      if (RUN_KEY) try { sessionStorage.removeItem(RUN_KEY); } catch {}
    } finally {
      isEnrichingRef.current = false;
    }
  };

  // Toggle filter selection per finding
  const toggleFilter = (fid) => {
    setFilterSelections(prev => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  };
  const filterSelectAll  = () => setFilterSelections(new Set(findings.map(f => f.id || f.url)));
  const filterClearAll   = () => setFilterSelections(new Set());

  // Stage 3 — Threat Assessment + AI Narrative (auto-fire setelah Decision 2)
  const runStage3 = async () => {
    if (!subject?.nik) return;
    setStage3Error(null);
    setState('stage_3_running');

    // Build filtered findings list
    const filteredFindings = findings.filter(f => filterSelections.has(f.id || f.url));

    // Aggregate emails + phones dari findings (untuk breach check)
    const emails = [...new Set(filteredFindings
      .map(f => f.raw_data?.email || f.raw_data?.contact_email)
      .filter(Boolean))].slice(0, 5);
    const phones = [...new Set(filteredFindings
      .map(f => f.raw_data?.phone || f.raw_data?.phone_number)
      .filter(Boolean))].slice(0, 3);

    // Aggregate candidate photos untuk reverse image
    const candidatePhotos = filteredFindings
      .flatMap(f => (f.raw_data?.image_urls || []).map(u => ({ photo_url: u, platform: f.platform })))
      .slice(0, 20);

    try {
      const r = await fetch('/api/soulsearch-scan', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body:    JSON.stringify({
          subject_id:       subject.nik,
          subject_name:     subject.nama_lengkap,
          photo_url:        subject.foto_url,
          candidate_photos: candidatePhotos,
          emails,
          phones,
          findings:         filteredFindings,
        }),
        signal: AbortSignal.timeout(120000),
      });
      if (!r.ok) throw new Error(`http_${r.status}`);
      const data = await r.json();

      setThreatData(data.threat || null);
      setBreachData(data.breach?.breaches || []);
      setNetworkEdges(data.edges || []);
      setShowSoulSearch(true);
      setState('complete');
    } catch (err) {
      console.warn('[stage3] failed:', err.message);
      setStage3Error(err.message);
      setState('complete'); // tetap show findings, threat panel kosong
    }
  };

  // B-002 — Generate Forensic Analysis Report (DOCX + PDF)
  const generateForensicReport = async () => {
    if (!subject?.nik || forensicGenerating) return;
    setForensicGenerating(true);
    setForensicError(null);
    setForensicResult(null);
    try {
      const r = await fetch('/api/forensic-report-generate', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body:    JSON.stringify({
          subject_id:       subject.nik,
          subject_name:     subject.nama_lengkap,
          operator_id:      operatorId,
          report_type:      forensicReportType,
          classification:   forensicClassification,
          language:         lang,
          include_findings:   true,
          include_face_match: true,
          include_breach:     true,
          selected_finding_ids: Array.from(filterSelections),
          // B-002 acceptance #3: operator-selected photos URLs.
          // Vercel handler akan download + base64 encode sebelum forward ke HF Space.
          selected_photo_urls: Array.from(forensicSelectedPhotos),
          output_format:    'both',
        }),
        signal: AbortSignal.timeout(180000),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.message || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setForensicResult(data);
      setShowForensicModal(false);
    } catch (err) {
      console.warn('[forensic] failed:', err.message);
      setForensicError(err.message);
    } finally {
      setForensicGenerating(false);
    }
  };

  // Toggle candidate selection
  const toggleCandidate = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(candidates.map(c => c.id)));
  const clearAll  = () => setSelectedIds(new Set());

  // Toggle data type
  const toggleDataType = (type) => {
    setDataTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  // Flag finding (save / wrong-match)
  const handleFlagFinding = async (finding, action) => {
    await flagFinding(finding.id, action, operatorId, subject.nik);
    if (action === 'wrong_match') {
      setFindings(prev => prev.filter(f => f.id !== finding.id));
    }
  };

  // GAP-3: Per-finding Save toggle — operator control individual finding
  // persist via osint_findings.is_saved (default true). Toggle off = exclude
  // dari saved record + report exports.
  const handleToggleSave = async (finding) => {
    const newValue = !finding.is_saved;
    const { error } = await supabase
      .from('osint_findings')
      .update({ is_saved: newValue })
      .eq('id', finding.id);
    if (error) {
      console.warn('[osint] save toggle fail:', error.message);
      return;
    }
    setFindings(prev => prev.map(f => f.id === finding.id ? { ...f, is_saved: newValue } : f));
  };

  // GAP-4: Location origin label per spec § 4.4 address fallback chain
  const locationOriginLabel = (source, lang) => {
    const map = {
      bio: {
        label: lang === 'id' ? 'Dari bio — diungkapkan sendiri' : 'From bio — self-disclosed',
        color: 'var(--color-green)',
        elevated: false,
      },
      bio_residential: {
        label: lang === 'id' ? 'Alamat di bio — diungkapkan sendiri' : 'Address in bio — self-disclosed',
        color: 'var(--color-green)',
        elevated: false,
      },
      profile_location: {
        label: lang === 'id' ? 'Lokasi profil — diungkapkan sendiri' : 'Profile location — self-disclosed',
        color: 'var(--color-green)',
        elevated: false,
      },
      self_specified: {
        label: lang === 'id' ? 'Lokasi profil — diungkapkan sendiri' : 'Profile location — self-disclosed',
        color: 'var(--color-green)',
        elevated: false,
      },
      post_tagged: {
        label: lang === 'id' ? 'Lokasi dari postingan — sensitif' : 'Post-tagged location — sensitive',
        color: 'var(--color-yellow)',
        elevated: true,
      },
    };
    return map[source] || null;
  };

  // Reset to never_run for re-run
  const handleReset = () => {
    setState('never_run');
    setRunId(null);
    setCandidates([]);
    setSelectedIds(new Set());
    setError(null);
    // G-003 async-safe: clear persisted runId
    if (RUN_KEY) try { sessionStorage.removeItem(RUN_KEY); } catch {}
  };

  // Manual purge — admin permanent delete semua data OSINT subject ini.
  // Audit log written di backend purgeSubject().
  const handlePurge = async () => {
    const confirmMsg = lang === 'id'
      ? `KONFIRMASI HAPUS PERMANEN\n\nTindakan ini akan menghapus SEMUA data Digital Forensic untuk ${subject?.nama_lengkap || subject?.nik} secara permanen dan tidak dapat dibatalkan.\n\nData wajah dan kendaraan tidak terpengaruh.\n\nLanjutkan?`
      : `CONFIRM PERMANENT DELETION\n\nThis will permanently delete ALL Digital Forensic data for ${subject?.nama_lengkap || subject?.nik}. This cannot be undone.\n\nFace and vehicle data unaffected.\n\nContinue?`;
    if (!window.confirm(confirmMsg)) return;
    const reason = window.prompt(
      lang === 'id' ? 'Alasan penghapusan (untuk audit log):' : 'Reason for deletion (for audit log):',
      ''
    );
    if (reason === null) return; // user cancelled prompt
    const result = await purgeSubject(subject.nik, operatorId, reason || 'admin_manual_purge');
    if (result.error) {
      setError(result.error);
      return;
    }
    // Reset UI ke never_run + clear local state
    setState('never_run');
    setRunId(null);
    setCandidates([]);
    setSelectedIds(new Set());
    setFindings([]);
    setSummary(null);
    setError(null);
    alert(
      lang === 'id'
        ? `Berhasil dihapus:\n- ${result.runs_deleted} run\n- ${result.candidates_deleted} kandidat\n- ${result.findings_deleted} temuan`
        : `Successfully deleted:\n- ${result.runs_deleted} runs\n- ${result.candidates_deleted} candidates\n- ${result.findings_deleted} findings`
    );
  };

  // Iterative enrichment — saat state=complete, fetch run candidates yang
  // is_selected=false (belum di-enrich) → operator bisa pilih + enrich tambahan.
  const handleEnrichMore = async () => {
    if (!runId) return;
    if (isEnrichingRef.current) {
      console.log('[enrich-more] already running, ignoring duplicate trigger');
      return;
    }
    isEnrichingRef.current = true;
    try {
      const result = await getCandidates(runId);
      if (result.error) { setError(result.error); return; }
      const unenriched = (result.candidates || []).filter(c => !c.is_selected);
      if (unenriched.length === 0) {
        alert(lang === 'id' ? 'Semua kandidat run ini sudah di-enrich.' : 'All candidates from this run are already enriched.');
        return;
      }
      setCandidates(unenriched);
      setSelectedIds(new Set());
      setState('awaiting_selection');
    } finally {
      isEnrichingRef.current = false;
    }
  };

  // ─── G-007 Export Word/PDF Report ─────────────────────────────────────────
  // Watermark operator + timestamp, classification stamp, ELEVATED markers,
  // audit log entry, daily rate limit (20/op/day per G-008).
  const handleExportWord = async () => {
    const quota = await checkReportQuota(operatorId);
    if (!quota.allowed) {
      setError(lang === 'id'
        ? `Kuota laporan harian tercapai (${quota.used}/${quota.limit}). Coba besok.`
        : `Daily report quota reached (${quota.used}/${quota.limit}). Try tomorrow.`);
      return;
    }
    try {
      await exportWordReport({ subject, findings, summary, operatorId, lang });
    } catch (err) {
      setError((lang === 'id' ? 'Gagal generate Word: ' : 'Word generation failed: ') + err.message);
    }
  };
  const handleExportPdf = async () => {
    const quota = await checkReportQuota(operatorId);
    if (!quota.allowed) {
      setError(lang === 'id'
        ? `Kuota laporan harian tercapai (${quota.used}/${quota.limit}). Coba besok.`
        : `Daily report quota reached (${quota.used}/${quota.limit}). Try tomorrow.`);
      return;
    }
    try {
      await exportPdfReport({ subject, findings, summary, operatorId, lang });
    } catch (err) {
      setError((lang === 'id' ? 'Gagal generate PDF: ' : 'PDF generation failed: ') + err.message);
    }
  };

  // ─── Legacy Export (JSON/CSV) — preserved untuk admin debug ────────────────
  const exportJSON = () => {
    const payload = {
      meta: {
        subject_nik:    subject?.nik,
        subject_name:   subject?.nama_lengkap,
        operator_id:    operatorId,
        exported_at:    new Date().toISOString(),
        total_findings: findings.length,
        retention_note: 'Default retention 24 months per UU PDP § 4.7',
      },
      summary: summary || null,
      findings: findings.map(f => ({
        id:             f.id,
        platform:       f.platform,
        source_type:    f.source_type,
        source_url:     f.source_url,
        confidence:     f.confidence,
        signal_explain: f.signal_explain,
        match_signals:  f.match_signals,
        retrieved_at:   f.retrieved_at,
        expires_at:     f.expires_at,
        is_stale:       f.is_stale,
        adapter:        f.adapter,
        raw_data:       f.raw_data,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `osint-report-${subject?.nik || 'subject'}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const headers = ['Platform', 'Source Type', 'URL', 'Confidence', 'Signal', 'Retrieved At', 'Adapter'];
    const rows = findings.map(f => [
      f.platform || '',
      f.source_type || '',
      f.source_url || '',
      ((f.confidence || 0) * 100).toFixed(1) + '%',
      (f.signal_explain || '').replace(/"/g, '""'),
      f.retrieved_at || '',
      f.adapter || '',
    ]);
    const csv = [
      `# Digital Forensic Report — ${subject?.nama_lengkap || subject?.nik}`,
      `# Exported: ${new Date().toISOString()}`,
      `# Operator: ${operatorId}`,
      '',
      headers.join(','),
      ...rows.map(r => r.map(v => `"${v}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `osint-report-${subject?.nik || 'subject'}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  return (
    <div className="flex flex-col gap-4 p-4">

      {/* Score Breakdown Popover — Google Docs Section 4.2.
          Fixed top-right viewport — visible saat operator hover HIGH tile. */}
      {breakdownEntry && breakdownEntry._composite && (
        <div style={{
          position: 'fixed', top: 80, right: 20, zIndex: 9999,
          pointerEvents: 'none',
        }}>
          <CandidateScoreBreakdown composite={breakdownEntry._composite} />
        </div>
      )}

      {/* Header — Summary jika ada riwayat */}
      {summary && summary.total_runs > 0 && (
        <div className="rounded-xl p-3 flex flex-wrap items-center gap-x-4 gap-y-1.5"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'Total run:' : 'Total runs:'}
            </span>
            <span className="text-[10px] font-bold font-mono" style={{ color: 'var(--text-hi)' }}>{summary.total_runs}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'Temuan tersimpan:' : 'Saved findings:'}
            </span>
            <span className="text-[10px] font-bold font-mono" style={{ color: 'var(--color-cyan)' }}>{summary.saved_findings}</span>
          </div>
          {summary.stale_findings > 0 && (
            <div className="flex items-center gap-1.5">
              <Calendar size={10} color="#ffd60a" />
              <span className="text-[10px] font-bold font-mono" style={{ color: 'var(--color-yellow)' }}>
                {summary.stale_findings} {lang === 'id' ? 'kedaluwarsa (>90 hari)' : 'stale (>90 days)'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Audit badge — OSINT terotorisasi untuk SEMUA subject yang di-input
          oleh operator authorized (consent + government authorization basis legal). */}
      <div className="rounded-xl p-3 flex items-start gap-2.5"
        style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.3)' }}>
        <Check size={14} color="var(--color-green)" className="shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: 'var(--color-green)' }}>
            {lang === 'id' ? 'Digital Forensic Terotorisasi' : 'Digital Forensic Authorized'}
          </p>
          <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-mid)' }}>
            {lang === 'id'
              ? 'Digital Forensic beroperasi dengan otorisasi pemerintah aktif. Setiap subject yang di-input ke sistem dianggap sudah memberikan konsen via mekanisme operator authorized. Audit log tersimpan otomatis sesuai UU PDP.'
              : 'Digital Forensic operates under active government authorization. Every subject entered into the system is presumed consented via authorized operator workflow. Audit log recorded automatically per UU PDP.'}
          </p>
        </div>
      </div>

      {/* STATE: never_run */}
      {state === 'never_run' && (
        <div className="rounded-xl p-6 flex flex-col items-center gap-4 text-center"
          style={{ background: 'var(--color-surface)', border: '1px dashed var(--border-dim)' }}>
          <Search size={32} color="rgba(0,212,255,0.3)" />
          <div>
            <p className="text-[12px] font-black mb-1" style={{ color: 'var(--text-hi)' }}>
              {lang === 'id' ? 'Digital Forensic' : 'Digital Forensic'}
            </p>
            <p className="text-[10px] font-mono leading-relaxed max-w-md" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id'
                ? 'Cari profil sosial media & jejak digital subjek. Operator review setiap kandidat sebelum pengayaan data.'
                : 'Search social media profiles & digital traces. Operator reviews each candidate before enrichment.'}
            </p>
          </div>
          {/* G-005: Radar Status — per-platform health indicator dgn recovery ETA */}
          {healthMap.size > 0 && (
            <div className="w-full max-w-md flex flex-wrap gap-1.5 justify-center pt-1">
              {[...healthMap.entries()].map(([platform, h]) => {
                const b = statusBadge(h.status, lang);
                // Recovery formula: berapa run sukses lagi sampai HEALTHY (rate ≥0.70)
                // (success + N) / (total + N) ≥ 0.70 → N ≥ (0.7T - S) / 0.30
                const successCount = Math.round(h.success_rate * h.total);
                const runsToHealthy = h.status === 'HEALTHY'
                  ? 0
                  : Math.max(1, Math.ceil((0.70 * h.total - successCount) / 0.30));
                const lastSuccessAgo = h.last_success_ts
                  ? Math.round((Date.now() - new Date(h.last_success_ts).getTime()) / 60000)
                  : null;
                const lastFailureAgo = h.last_failure_ts
                  ? Math.round((Date.now() - new Date(h.last_failure_ts).getTime()) / 60000)
                  : null;
                const tooltip = [
                  `${platform.toUpperCase()}: ${b.label}`,
                  `Stats 24h: ${successCount}/${h.total} sukses (${Math.round(h.success_rate * 100)}%)`,
                  h.consecutive_failures > 0 ? `Consecutive fail: ${h.consecutive_failures}` : null,
                  lastSuccessAgo !== null ? `Last success: ${lastSuccessAgo}m ago` : 'Belum ada success record',
                  lastFailureAgo !== null ? `Last failure: ${lastFailureAgo}m ago` : null,
                  h.status !== 'HEALTHY' ? `\nButuh ~${runsToHealthy} run sukses lagi → HEALTHY` : null,
                ].filter(Boolean).join('\n');
                return (
                  <span key={platform}
                    title={tooltip}
                    className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ background: b.color + '15', color: b.color, border: `1px solid ${b.color}40` }}>
                    <span>{platform.toUpperCase()} · {b.label}</span>
                    {h.status !== 'HEALTHY' && h.status !== 'UNKNOWN' && (
                      <span style={{ opacity: 0.7, fontWeight: 400 }}>
                        · {runsToHealthy}r
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {/* GAP-2: Optional inputs — username + city + employer */}
          <div className="w-full max-w-md flex flex-col gap-2">
            <input
              value={usernameHint}
              onChange={e => setUsernameHint(e.target.value)}
              placeholder={lang === 'id' ? 'Username (opsional, bantu akurasi)' : 'Username hint (optional, boosts accuracy)'}
              className="text-[10px] font-mono px-3 py-2 rounded-lg outline-none"
              style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-hi)' }}
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                value={cityHint}
                onChange={e => setCityHint(e.target.value)}
                placeholder={lang === 'id' ? 'Kota (opsional)' : 'City (optional)'}
                className="text-[10px] font-mono px-3 py-2 rounded-lg outline-none"
                style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-hi)' }}
              />
              <input
                value={employerHint}
                onChange={e => setEmployerHint(e.target.value)}
                placeholder={lang === 'id' ? 'Instansi (opsional)' : 'Employer (optional)'}
                className="text-[10px] font-mono px-3 py-2 rounded-lg outline-none"
                style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-hi)' }}
              />
            </div>
            {/* Spec 2026-05-09: keywords[] max 5 — chip-style input */}
            <div style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', borderRadius: 8, padding: 8 }}>
              <div className="flex flex-wrap items-center gap-1.5">
                {keywords.map((kw, idx) => (
                  <span key={idx} className="text-[9px] font-mono px-2 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ background: 'var(--tint)', color: 'var(--color-cyan)', border: '1px solid var(--border-dim)' }}>
                    {kw}
                    <button type="button"
                      onClick={() => setKeywords(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'transparent', border: 'none', color: 'var(--color-cyan)', cursor: 'pointer', padding: 0, fontSize: 11, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                {keywords.length < 5 && (
                  <input
                    value={keywordDraft}
                    onChange={e => setKeywordDraft(e.target.value)}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ',') && keywordDraft.trim() && keywords.length < 5) {
                        e.preventDefault();
                        setKeywords(prev => [...prev, keywordDraft.trim()]);
                        setKeywordDraft('');
                      } else if (e.key === 'Backspace' && !keywordDraft && keywords.length > 0) {
                        setKeywords(prev => prev.slice(0, -1));
                      }
                    }}
                    placeholder={keywords.length === 0
                      ? (lang === 'id' ? 'Kata kunci (opsional, max 5 — Enter untuk tambah)' : 'Keywords (optional, max 5 — Enter to add)')
                      : (lang === 'id' ? `+ tambah (${5 - keywords.length} sisa)` : `+ add (${5 - keywords.length} left)`)}
                    className="text-[10px] font-mono outline-none flex-1 min-w-[120px]"
                    style={{ background: 'transparent', color: 'var(--text-hi)', border: 'none', padding: '2px 0' }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* GAP-1: Platform selection checkboxes — 10 platforms */}
          <div className="w-full max-w-md">
            <p className="text-[9px] font-mono font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-dim)' }}>
              {lang === 'id' ? 'Pilih Platform untuk Pencarian' : 'Select Platforms to Search'}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: 'instagram', label: 'Instagram',   icon: 'IG' },
                { id: 'facebook',  label: 'Facebook',    icon: 'FB' },
                { id: 'tiktok',    label: 'TikTok',      icon: 'TT' },
                { id: 'x',         label: 'X / Twitter', icon: 'X' },
                { id: 'linkedin',  label: 'LinkedIn',    icon: 'LI' },
                { id: 'threads',   label: 'Threads',     icon: 'TH' },
                { id: 'youtube',   label: 'YouTube',     icon: 'YT' },
                { id: 'web',       label: lang === 'id' ? 'Web Search' : 'Web Search', icon: 'WEB' },
                { id: 'news',      label: lang === 'id' ? 'Berita'      : 'News',       icon: 'NEWS' },
                { id: 'maps',      label: 'Google Maps', icon: 'MAP' },
              ].map(p => {
                const active = selectedPlatforms.has(p.id);
                return (
                  <button key={p.id}
                    type="button"
                    onClick={() => {
                      setSelectedPlatforms(prev => {
                        const next = new Set(prev);
                        if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                        return next;
                      });
                    }}
                    className="flex items-center gap-2 text-[10px] font-mono font-bold px-2.5 py-1.5 rounded-lg transition-all"
                    style={{
                      background: active ? 'rgba(0,212,255,0.15)' : 'var(--color-card)',
                      border: `1px solid ${active ? 'var(--color-cyan)' : 'var(--border-dim)'}`,
                      color: active ? 'var(--color-cyan)' : 'var(--text-mute)',
                    }}>
                    {active && <Check size={10} />}
                    <span>{p.icon}</span>
                    <span>{p.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[8px] font-mono mt-2" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id'
                ? `${selectedPlatforms.size}/10 platform dipilih · platform tidak dipilih = 0 cost`
                : `${selectedPlatforms.size}/10 platforms selected · unselected = 0 cost`}
            </p>
          </div>
          {/* Phase 1+6: HF Status indicator + admin manual warmup button */}
          <div className="flex items-center gap-2 text-[9px] font-mono"
            style={{ color: 'var(--text-mute)' }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: ({
                ready:    'var(--color-green)',
                warming:  'var(--color-yellow)',
                cold:     'var(--color-orange)',
                sleeping: 'var(--color-red)',
                unknown:  'var(--text-mute)',
              })[hfStatus] || 'var(--text-mute)',
              boxShadow: hfStatus === 'ready' ? '0 0 8px var(--color-green)' : 'none',
              transition: 'all 0.3s',
            }} />
            <span style={{ color: hfStatus === 'ready' ? 'var(--color-green)' : 'var(--text-mute)' }}>
              {({
                ready:    lang === 'id' ? 'KES Engine siap'                 : 'KES Engine ready',
                warming:  lang === 'id' ? 'KES Engine memulai...'           : 'KES Engine warming up...',
                cold:     lang === 'id' ? 'KES Engine bangun...'            : 'KES Engine waking...',
                sleeping: lang === 'id' ? 'KES Engine tidur — bangunkan...' : 'KES Engine sleeping — waking...',
                unknown:  lang === 'id' ? 'Memeriksa KES Engine...'         : 'Checking KES Engine...',
              })[hfStatus] || ''}
            </span>
            <button
              type="button"
              onClick={async () => {
                setHfStatus('warming');
                const t0 = Date.now();
                try {
                  const r = await fetch('/api/hf-health');
                  const elapsed = Date.now() - t0;
                  if (!r.ok) {
                    setHfStatus(r.status === 502 ? 'sleeping' : 'unknown');
                    return;
                  }
                  setHfStatus(elapsed > 5000 ? 'cold' : elapsed > 1000 ? 'warming' : 'ready');
                } catch { setHfStatus('unknown'); }
              }}
              title={lang === 'id' ? 'Aktifkan Mode Demo — panaskan engine' : 'Activate Demo Mode — warm up engine'}
              className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded hover:opacity-80"
              style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)', border: '1px solid rgba(0,212,255,0.3)' }}>
              {lang === 'id' ? '▸ Aktifkan' : '▸ Activate'}
            </button>
          </div>
          <button
            onClick={() => {
              // Kalau dipanggil dari workspace (autoStart), langsung jalan inline.
              // Kalau dari modal NIK (operator klik di card detail), broadcast event
              // ke parent untuk pindah ke tab "Digital Forensic" dengan subject pre-loaded.
              if (autoStart) {
                runOsintStream();
              } else {
                window.dispatchEvent(new CustomEvent('horus:open-forensic', { detail: { subject } }));
              }
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black transition-all hover:opacity-90"
            style={{ background: 'var(--color-cyan)', color: 'var(--color-bg)' }}>
            <Search size={12} />
            {lang === 'id' ? 'Jalankan Digital Forensic' : 'Run Digital Forensic'}
          </button>
          <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
            {lang === 'id'
              ? 'Engine: Netcomm Digital Forensic'
              : 'Engine: Netcomm Digital Forensic'}
          </p>
        </div>
      )}

      {/* THEATER MODE 3-column (Sprint 007 ui_reference)
          Layout per spec: Discovery Signals | Intelligence Findings | Subject + Matches.
          GATE: streamMode + pipeline aktif/complete + belum di-dismiss operator.
          Operator harus klik "LANJUT KE STAGE 2" untuk advance — tidak auto. */}
      {streamMode && !theaterDismissed && ['warming','stage1','discovered','stage2','face','complete','error'].includes(pipelinePhase) && (
        <div className="rounded-xl p-5"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-mid)',
            boxShadow: '0 0 24px var(--tint)' }}>

          {/* Theater Header */}
          <div className="flex items-center justify-between mb-4 pb-3"
            style={{ borderBottom: '1px solid var(--border-dim)' }}>
            <div>
              <p className="text-[12px] font-black uppercase tracking-widest" style={{ color: 'var(--color-cyan)' }}>
                STAGE 1 — DISCOVERY & LIVE BIOMETRIC MATCHING
              </p>
              <p className="text-[9px] font-mono mt-0.5" style={{ color: 'var(--text-mute)' }}>
                {lang === 'id'
                  ? 'Pemindaian otonom · Identitas biometrik · Pencocokan wajah lintas platform'
                  : 'Autonomous scan · Biometric identity · Cross-platform face matching'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(0,255,136,0.10)', color: 'var(--color-green)',
                border: '1px solid rgba(0,255,136,0.4)',
              }}>
                <span className="animate-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-green)', boxShadow: '0 0 6px var(--color-green)' }} />
                LIVE
              </span>
              <span className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>
                <Stage1ElapsedTimer isComplete={pipelinePhase === 'complete'} />
              </span>
            </div>
          </div>

          {/* RIS (Reverse Image Search Yandex) status banner — alias detection layer */}
          {risStatus.status !== 'idle' && (
            <div style={{
              marginBottom: 12, padding: '6px 10px', borderRadius: 6,
              background: risStatus.status === 'failed' ? 'rgba(255,107,43,0.08)' : 'var(--tint)',
              border: `1px solid ${risStatus.status === 'failed' ? 'var(--color-orange)' : 'var(--border-mid)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            }}>
              <div className="flex items-center gap-2">
                {risStatus.status === 'running' && <Loader2 size={11} className="animate-spin" color="var(--color-cyan)" />}
                {risStatus.status === 'done'    && <Check size={11} color="var(--color-green)" />}
                {risStatus.status === 'failed'  && <AlertTriangle size={11} color="var(--color-orange)" />}
                <span className="text-[9px] font-mono uppercase tracking-widest" style={{
                  color: risStatus.status === 'failed' ? 'var(--color-orange)' : 'var(--color-cyan)',
                  fontWeight: 700,
                }}>
                  {lang === 'id' ? '▸ PENCOCOKAN WAJAH LINTAS PLATFORM ·' : '▸ CROSS-PLATFORM FACE MATCHING ·'}
                  {risStatus.status === 'running' && (lang === 'id' ? ' mencari wajah subject di seluruh web...' : ' searching subject face across the web...')}
                  {risStatus.status === 'done' && (lang === 'id'
                    ? ` ${risStatus.additional_candidates} kandidat tambahan ditemukan dari ${risStatus.total_hits} halaman web`
                    : ` ${risStatus.additional_candidates} additional candidates found from ${risStatus.total_hits} web pages`)}
                  {risStatus.status === 'failed' && (lang === 'id'
                    ? ` ${risStatus.reason || 'gagal'} — lanjut text-only discovery`
                    : ` ${risStatus.reason || 'failed'} — continuing text-only discovery`)}
                </span>
              </div>
              <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                Alias detection layer
              </span>
            </div>
          )}

          {/* 3-COLUMN GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: '24% 28% 48%', gap: 12 }}>

            {/* COL 1: DISCOVERY SIGNALS */}
            <div style={{ minHeight: 400, background: 'var(--color-card)', borderRadius: 8, padding: 10, border: '1px solid var(--border-dim)' }}>
              <p className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--color-cyan)' }}>
                ▸ DISCOVERY SIGNALS
              </p>
              <p className="text-[8px] font-mono mb-3" style={{ color: 'var(--text-mute)' }}>
                Ranked by relevance score
              </p>
              <div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
                {/* RADAR A/B/C/D — discovery engine signals. Polish 2026-05-19:
                    per-radar pulsing ring (left edge) saat 'running', radar-blip
                    progress beam at progress bar, RADAR-D image SERP added. */}
                {(() => {
                  // Radar metadata — color-coded per engine type
                  const RADARS = [
                    { id: 'radar_a', code: 'R1', engine: '',     color: 'var(--color-purple)' },
                    { id: 'radar_b', code: 'R2', engine: '',  color: '#7c4dff' },
                    { id: 'radar_c', code: 'R3', engine: '', color: '#ff6b2b' },
                    { id: 'radar_d', code: 'R4', engine: '',   color: 'var(--color-cyan)' },
                  ];
                  const anyRadarActive = RADARS.some(r => platformStates[r.id]);
                  if (!anyRadarActive) return null;
                  return RADARS.map((radar, idx) => {
                    const tile = platformStates[radar.id] || { phase: 'idle', count: 0 };
                    // RADAR-D uses face_matches_above_threshold as primary signal
                    const primaryCount = radar.id === 'radar_d'
                      ? (tile.face_matches_above_threshold ?? tile.count ?? 0)
                      : (tile.count ?? 0);
                    const score = tile.phase === 'done' && primaryCount > 0
                      ? Math.min(100, Math.round((primaryCount / 20) * 100))
                      : tile.phase === 'partial' ? 50
                      : tile.phase === 'running' ? 30
                      : 0;
                    const isRunning = tile.phase === 'running' || tile.phase === 'partial';
                    const isDone = tile.phase === 'done';
                    const isLastRadar = idx === RADARS.length - 1;
                    return (
                      <div key={radar.id} className="animate-tile-arrive flex items-start gap-2"
                        style={{ paddingBottom: 4, marginBottom: 2, borderBottom: isLastRadar ? '1px dashed var(--border-dim)' : 'none', position: 'relative' }}>
                        {/* Pulsing ring at left edge while running */}
                        <div style={{
                          position: 'relative', minWidth: 22, display: 'flex',
                          alignItems: 'center', justifyContent: 'flex-start',
                        }}>
                          <span style={{
                            fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                            color: radar.color, position: 'relative', zIndex: 2,
                          }}>{radar.code}</span>
                          {isRunning && (
                            <span className="animate-radar-ring" style={{
                              position: 'absolute', left: -3, top: '50%',
                              width: 14, height: 14, borderRadius: '50%',
                              transform: 'translateY(-50%)',
                              color: radar.color, // currentColor utk box-shadow di keyframes
                            }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className="text-[9px] font-bold truncate flex items-center gap-1" style={{ color: 'var(--text-hi)' }}>
                            <span>{PLATFORM_LABEL[radar.id] || radar.id}</span>
                            <span style={{
                              fontSize: 6, fontFamily: 'IBM Plex Mono',
                              color: radar.color, opacity: 0.7,
                              padding: '0 3px', borderRadius: 2,
                              background: `${radar.color}15`, letterSpacing: 0.5,
                            }}>{radar.engine}</span>
                          </p>
                          <div className="flex items-center gap-2 mt-1" style={{ position: 'relative' }}>
                            <div style={{
                              flex: 1, height: 4, background: 'var(--tint)',
                              borderRadius: 2, overflow: 'hidden', position: 'relative',
                            }}>
                              <div style={{
                                width: `${score}%`, height: '100%',
                                background: isDone ? 'var(--color-green)' : radar.color,
                                transition: 'width 0.5s ease',
                                boxShadow: isDone ? `0 0 6px ${radar.color}88` : 'none',
                              }} />
                              {/* Radar blip beam — only while running, sweeps across bar */}
                              {isRunning && (
                                <div className="animate-radar-blip" style={{
                                  position: 'absolute', top: 0, bottom: 0,
                                  width: '30%',
                                  background: `linear-gradient(90deg, transparent, ${radar.color}cc, transparent)`,
                                  pointerEvents: 'none',
                                }} />
                              )}
                            </div>
                            {/* Animated counter — animate-ticker-up on count change via key */}
                            <span key={`${radar.id}-${primaryCount}`} className="animate-ticker-up text-[8px] font-mono"
                              style={{ color: isDone ? 'var(--color-green)' : 'var(--text-mute)', fontWeight: 700, minWidth: 28, textAlign: 'right' }}>
                              {primaryCount > 0 ? `${primaryCount}` : `${score}%`}
                            </span>
                          </div>
                          <p className="text-[7px] font-mono mt-0.5" style={{ color: 'var(--text-mute)' }}
                            title={tile.reason || ''}>
                            {tile.phase === 'idle' && 'Standby...'}
                            {tile.phase === 'running' && (radar.id === 'radar_d'
                              ? `Scanning ${tile.total_images ? `0/${tile.total_images}` : ''} images...`
                              : 'Scanning...')}
                            {tile.phase === 'partial' && (radar.id === 'radar_d'
                              ? `${primaryCount} face hits / ${tile.total_images || '?'} scanned...`
                              : `${tile.count} hits...`)}
                            {tile.phase === 'done' && (radar.id === 'radar_d'
                              ? `✓ ${primaryCount} face matches / ${tile.count || 0} images`
                              : `✓ ${tile.count} hits`)}
                            {tile.phase === 'error' && (tile.reason ? `✕ ${tile.reason.slice(0, 35)}` : '✕ Error')}
                            {tile.phase === 'skip' && `— ${tile.reason || 'Skip'}`}
                          </p>
                        </div>
                      </div>
                    );
                  });
                })()}
                {/* Per-platform target signals */}
                {Array.from(selectedPlatforms).map((platform, idx) => {
                  if (platform === 'web') return null; // web sudah di-cover RADAR-A/B/C
                  const tile = platformStates[platform] || { phase: 'idle', count: 0 };
                  const score = tile.phase === 'done' && tile.count > 0
                    ? Math.min(100, Math.round((tile.count / 10) * 100))
                    : tile.phase === 'partial' ? 50 : 0;
                  return (
                    <div key={platform} className="animate-tile-arrive flex items-start gap-2">
                      <span style={{
                        fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                        color: 'var(--color-cyan)', minWidth: 18,
                      }}>{String(idx + 1).padStart(2, '0')}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="text-[9px] font-bold truncate" style={{ color: 'var(--text-hi)' }}>
                          {PLATFORM_LABEL[platform] || platform}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <div style={{ flex: 1, height: 3, background: 'var(--tint)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{
                              width: `${score}%`, height: '100%',
                              background: tile.phase === 'done' ? 'var(--color-green)' : 'var(--color-cyan)',
                              transition: 'width 0.5s ease',
                            }} />
                          </div>
                          <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>{score}%</span>
                        </div>
                        <p className="text-[7px] font-mono mt-0.5" style={{ color: 'var(--text-mute)' }}
                          title={tile.reason || ''}>
                          {tile.phase === 'idle' && 'Waiting...'}
                          {tile.phase === 'running' && 'Scanning...'}
                          {tile.phase === 'partial' && `${tile.count} found...`}
                          {tile.phase === 'done' && `✓ ${tile.count} results`}
                          {tile.phase === 'timeout' && `⚠ ${tile.count} partial — ${(tile.reason || 'budget exceeded').slice(0, 30)}`}
                          {tile.phase === 'error' && (tile.reason
                            ? `✕ ${tile.reason.slice(0, 35)}…`
                            : '✕ Error')}
                          {tile.phase === 'skip' && `— Skip${tile.reason ? ` (${tile.reason.slice(0,20)})` : ''}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* COL 2: INTELLIGENCE FINDINGS */}
            <div style={{ minHeight: 400, background: 'var(--color-card)', borderRadius: 8, padding: 10, border: '1px solid var(--border-dim)' }}>
              <p className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: 'var(--color-cyan)' }}>
                ▸ INTELLIGENCE FINDINGS
              </p>
              <p className="text-[8px] font-mono mb-3" style={{ color: 'var(--text-mute)' }}>
                Mentions · Articles · News · Posts
              </p>
              <div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-1">
                {faceMatchStream.length === 0 && Object.keys(platformStates).length === 0 && (
                  <p className="text-[9px] font-mono py-8 text-center" style={{ color: 'var(--text-mute)' }}>
                    Menunggu data discovery...
                  </p>
                )}
                {/* Issue 5 FIX: also render raw platform discovery items kalau face match
                    belum jalan / partial. Sebelumnya cuma faceMatchStream → kolom kosong
                    saat Facebook "3 partial". Sekarang merge: faceMatchStream + raw items
                    per platform, dedupe by URL. */}
                {(() => {
                  const seenUrls = new Set(faceMatchStream.map(e => e.photo_url).filter(Boolean));
                  const partialItems = [];
                  for (const [platform, state] of Object.entries(platformStates)) {
                    if (!Array.isArray(state.items) || state.items.length === 0) continue;
                    for (const item of state.items) {
                      const url = item.url || item.profile_url;
                      if (!url || seenUrls.has(url)) continue;
                      seenUrls.add(url);
                      // Skip kalau sudah jadi entry di faceMatchStream (handle match)
                      const handle = item.handle || item.username;
                      if (handle && faceMatchStream.find(fm => fm.handle === handle && fm.platform === platform)) continue;
                      partialItems.push({
                        platform,
                        title: item.title || handle || url,
                        snippet: item.snippet || item.description,
                        url,
                        handle,
                        is_partial: state.phase === 'partial' || state.phase === 'timeout',
                      });
                    }
                  }
                  return partialItems.slice(0, 6).map((it, idx) => (
                    <div key={`partial-${it.url}`} className="animate-tile-arrive flex items-start gap-2">
                      <span style={{
                        fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                        color: 'var(--text-mute)', minWidth: 18,
                      }}>{String(faceMatchStream.length + idx + 1).padStart(2, '0')}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="text-[9px] truncate" style={{ color: 'var(--text-hi)' }}>
                          {it.title}
                          {it.is_partial && (
                            <span style={{
                              marginLeft: 6, fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                              padding: '0 3px', borderRadius: 2, letterSpacing: 0.5,
                              background: 'var(--color-yellow)', color: 'var(--color-bg)',
                            }} title="Discovery partial — face match belum selesai">
                              PARTIAL
                            </span>
                          )}
                        </p>
                        <p className="text-[7px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-mute)' }}>
                          {SOURCE_BRAND_LABEL[it.platform] || (it.platform || '').toUpperCase()}
                          {it.handle && <span> · @{it.handle}</span>}
                        </p>
                        {it.snippet && (
                          <p className="text-[6px] mt-0.5 truncate" style={{ color: 'var(--text-mute)' }}>
                            {it.snippet}
                          </p>
                        )}
                      </div>
                    </div>
                  ));
                })()}
                {/* Live findings dari face match results — show platform/handle + similarity */}
                {faceMatchStream.slice(0, 12).map((entry, idx) => {
                  const tierColor = ({
                    strong: 'var(--color-green)', match: 'var(--color-cyan)',
                    possible: 'var(--color-yellow)', unlikely: 'var(--text-mute)', failed: 'var(--color-red)',
                  })[entry.tier] || 'var(--text-mute)';
                  // V-001 spec: explicit tier badges (STRONG/MATCH/POSSIBLE/UNLIKELY/NO FACE)
                  const tierLabel = ({
                    strong: 'STRONG', match: 'MATCH', possible: 'POSSIBLE',
                    unlikely: 'UNLIKELY', failed: 'NO FACE',
                  })[entry.tier];
                  return (
                    <div key={entry.id} className="animate-tile-arrive flex items-start gap-2">
                      <span style={{
                        fontSize: 8, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                        color: 'var(--color-cyan)', minWidth: 18,
                      }}>{String(idx + 1).padStart(2, '0')}</span>
                      {/* Issue 7 FIX: candidate profile photo thumbnail */}
                      {entry.photo_url && (
                        <div style={{
                          width: 24, height: 24, borderRadius: 4, overflow: 'hidden',
                          border: `1px solid ${tierColor}66`, flexShrink: 0,
                        }}>
                          <img src={entry.photo_url} alt={entry.handle || 'candidate'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={ev => { ev.target.parentElement.style.display = 'none'; }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="text-[9px] truncate" style={{ color: 'var(--text-hi)' }}>
                          {entry.display_name || entry.handle || '—'}
                          {entry.disguised && (
                            <span style={{
                              marginLeft: 6, fontSize: 7, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                              color: 'var(--color-orange)', letterSpacing: 0.5,
                              padding: '0 3px', borderRadius: 2,
                              background: 'rgba(255,107,43,0.12)',
                              border: '1px solid rgba(255,107,43,0.35)',
                            }} title="Face match ≥65% + nama akun jauh berbeda dari subject. Kemungkinan akun alias/disguised (V-003).">
                              ALIAS
                            </span>
                          )}
                        </p>
                        <p className="text-[7px] font-mono uppercase tracking-wider mt-0.5" style={{ color: 'var(--text-mute)' }}>
                          {SOURCE_BRAND_LABEL[entry.platform] || (entry.platform || '').toUpperCase()}
                          {entry.similarity != null && (
                            <span style={{ marginLeft: 6, color: tierColor, fontWeight: 700 }}>
                              · {Math.round(entry.similarity * 100)}%
                            </span>
                          )}
                          {tierLabel && (
                            <span style={{
                              marginLeft: 6, fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                              padding: '0 3px', borderRadius: 2, letterSpacing: 0.5,
                              background: `${tierColor}22`, color: tierColor,
                              border: `1px solid ${tierColor}55`,
                            }} title={`Tier ${entry.tier}: ${entry.tier === 'strong' ? '≥80%' : entry.tier === 'match' ? '60-80%' : entry.tier === 'possible' ? '45-60%' : entry.tier === 'unlikely' ? '<45%' : 'wajah tidak terdeteksi'}`}>
                              {tierLabel}
                            </span>
                          )}
                          {entry.auto_confirm && (
                            <span style={{
                              marginLeft: 6, fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                              padding: '0 3px', borderRadius: 2, letterSpacing: 0.5,
                              background: 'var(--color-green)', color: 'var(--color-bg)',
                            }} title="Auto-confirm threshold ≥85% terlampaui">
                              ✓ AUTO
                            </span>
                          )}
                        </p>
                        {/* V-002 per-signal breakdown — Nama / Foto / Username % */}
                        {(entry.name_match != null || entry.username_match != null || entry.photo_match != null) && (
                          <p className="text-[6px] font-mono mt-0.5 truncate" style={{ color: 'var(--text-mute)' }}>
                            {entry.name_match != null && (
                              <>Nama: <span style={{ color: 'var(--text-dim)', fontWeight: 700 }}>{Math.round(entry.name_match * 100)}%</span></>
                            )}
                            {entry.photo_match != null && (
                              <> · Foto: <span style={{ color: 'var(--text-dim)', fontWeight: 700 }}>{Math.round(entry.photo_match * 100)}%</span></>
                            )}
                            {entry.username_match != null && entry.username_match > 0 && (
                              <> · User: <span style={{ color: 'var(--text-dim)', fontWeight: 700 }}>{Math.round(entry.username_match * 100)}%</span></>
                            )}
                          </p>
                        )}
                        {/* V-001 spec: Live progress bar "Deep scan N/12" saat scanning */}
                        {entry.status === 'scanning' && entry.total_photos > 1 && (
                          <p className="text-[6px] font-mono mt-0.5 animate-pulse" style={{ color: 'var(--color-cyan)' }}>
                            ▶ Deep scan {entry.scanned || 0}/{entry.total_photos}
                          </p>
                        )}
                        {/* SoulSearch ala The Circle: photo strip preview — thumbnails
                            muncul progressively, border-color per tier similarity,
                            "scanning beam" pulse di photo terakhir saat scanning */}
                        {Array.isArray(entry.scanned_photos) && entry.scanned_photos.length > 0 && (
                          <div style={{
                            display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap',
                          }}>
                            {entry.scanned_photos.slice(0, 12).map((ph, phIdx) => {
                              const phPct = Math.round((ph.similarity || 0) * 100);
                              const phColor = ph.no_face ? 'rgba(120,120,120,0.4)'
                                : phPct >= 60 ? 'var(--color-cyan)'
                                : phPct >= 45 ? 'var(--color-green)'
                                : phPct >= 38 ? 'var(--color-yellow)'
                                : 'rgba(160,80,80,0.5)';
                              const isLatest = phIdx === entry.scanned_photos.length - 1 && entry.status === 'scanning';
                              return (
                                <div key={`${entry.id}-ph-${phIdx}`}
                                  className={`animate-tile-arrive ${isLatest ? 'animate-pulse' : ''}`}
                                  title={ph.no_face ? 'Wajah tidak terdeteksi' : `${phPct}% similarity`}
                                  style={{
                                    width: 22, height: 22, borderRadius: 3, overflow: 'hidden',
                                    border: `1.5px solid ${phColor}`,
                                    position: 'relative',
                                    boxShadow: phPct >= 60 ? `0 0 6px ${phColor}88` : 'none',
                                    flexShrink: 0,
                                  }}>
                                  <img src={ph.url} alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={ev => { ev.target.style.display = 'none'; ev.target.parentElement.style.background = 'rgba(80,80,80,0.4)'; }} />
                                  {phPct >= 38 && !ph.no_face && (
                                    <span style={{
                                      position: 'absolute', bottom: 0, right: 0,
                                      fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                                      background: phColor, color: 'var(--color-bg)',
                                      padding: '0 1px', lineHeight: 1.1,
                                    }}>{phPct}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* COL 3: SUBJECT CARD + HIGH/MEDIUM CONFIDENCE MATCHES */}
            <div style={{ minHeight: 400, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Subject Card */}
              <div style={{
                background: 'var(--color-card)', borderRadius: 8, padding: 12,
                border: '1px solid var(--border-hi)', position: 'relative',
              }}>
                <span style={{
                  position: 'absolute', top: -10, left: 12,
                  fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                  background: 'var(--color-cyan)', color: 'var(--color-bg)',
                  padding: '3px 10px', borderRadius: 999, letterSpacing: 1,
                }}>{(subject?.nama_lengkap || 'SUBJECT').toUpperCase()}</span>
                <div className="flex gap-3 mt-2">
                  <div style={{ flex: 1, fontSize: 9, fontFamily: 'IBM Plex Mono' }}>
                    <p style={{ color: 'var(--text-mute)' }}>NIK: <span style={{ color: 'var(--text-hi)' }}>{subject?.nik || '—'}</span></p>
                    <p style={{ color: 'var(--text-mute)' }}>LOKASI: <span style={{ color: 'var(--text-hi)' }}>{(subject?.kota_kabupaten || '—').toUpperCase()}</span></p>
                    <p style={{ color: 'var(--text-mute)' }}>STATUS: <span style={{ color: 'var(--text-hi)' }}>{(subject?.status_intelijen || 'CLEAR').toUpperCase()}</span></p>
                    <p style={{ color: 'var(--text-mute)' }}>BIOMETRIK: <span style={{ color: faceMatchSubject?.ready ? 'var(--color-green)' : 'var(--color-yellow)' }}>
                      {faceMatchSubject?.ready
                        ? (lang === 'id'
                            ? `✓ Identitas Terkunci · ${faceMatchSubject.embedding_count || (faceMatchSubject.all_photos || []).length} sudut wajah`
                            : `✓ Identity Locked · ${faceMatchSubject.embedding_count || (faceMatchSubject.all_photos || []).length} face angles`)
                        : (lang === 'id' ? '› memindai sudut wajah...' : '› scanning face angles...')}
                    </span></p>
                    {/* Biometric Gate warning per Google Docs Section 3.1 — score cap 60 */}
                    {(() => {
                      const gate = checkBiometricGateFromState(faceMatchSubject);
                      if (gate.available) return null;
                      return (
                        <p style={{
                          marginTop: 4, padding: '3px 6px', borderRadius: 4,
                          background: 'rgba(255, 214, 10, 0.08)',
                          border: '1px solid rgba(255, 214, 10, 0.4)',
                          color: 'var(--color-yellow)', fontSize: 8, fontFamily: 'IBM Plex Mono',
                        }}>
                          ⚠ {gate.warning}
                        </p>
                      );
                    })()}
                  </div>
                  {/* Render 5 slot fixed (DEPAN/KIRI/KANAN/ATAS/BAWAH) — slot
                      yang belum di-upload show placeholder kosong dgn label dim.
                      Sistem TETAP berfungsi dgn 1-5 foto: setiap pose enrolled
                      jadi reference embedding terpisah, MAX similarity wins.
                      Slot di-extract dari URL pattern penduduk-photos/{nik}/{slot}.{ext}. */}
                  <div className="grid grid-cols-3 gap-1" style={{ maxWidth: 168 }}>
                    {(() => {
                      // Build slot→url map dari foto enrolled.
                      // BUG FIX 2026-05-09: `[] || x` returns [] (truthy empty),
                      // so fallback never triggered when backend sends all_photos:[].
                      // Use explicit length check + multi-source fallback.
                      const slotMap = {};
                      const allCandidatePhotos = [
                        ...((Array.isArray(faceMatchSubject?.all_photos) && faceMatchSubject.all_photos.length > 0)
                          ? faceMatchSubject.all_photos
                          : []),
                        faceMatchSubject?.foto_url,
                        subject?.foto_url,
                      ].filter(Boolean);
                      const photos = [...new Set(allCandidatePhotos)];  // dedup
                      photos.forEach((url, i) => {
                        const m = (url || '').match(/\/(\d+)\.(jpg|jpeg|png|webp)(\?|$)/i);
                        const slot = m ? parseInt(m[1], 10) : i;
                        slotMap[slot] = url;
                      });
                      // Render fixed 5 slot — filled atau placeholder
                      return [0, 1, 2, 3, 4].map(slot => {
                        const url = slotMap[slot];
                        const label = POSE_LABELS[slot] || `FOTO ${slot + 1}`;
                        const filled = !!url;
                        return (
                          <div key={slot} style={{
                            width: 52, height: 52, borderRadius: 6, overflow: 'hidden',
                            border: `1px ${filled ? 'solid' : 'dashed'} ${filled ? 'var(--color-cyan)' : 'var(--border-dim)'}`,
                            position: 'relative',
                            background: filled ? 'transparent' : 'var(--color-surface)',
                            opacity: filled ? 1 : 0.45,
                          }}>
                            {filled
                              ? <img src={url} alt={`pose-${slot}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(ev) => {
                                    // Issue 2 fix: kalau image gagal load (CORS/404/expired
                                    // signed URL), fallback ke ○ glyph supaya operator tahu
                                    // visual ada masalah, bukan tile kosong tanpa indikasi.
                                    const parent = ev.target.parentElement;
                                    if (parent) {
                                      ev.target.style.display = 'none';
                                      const fallback = document.createElement('div');
                                      fallback.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:14px;color:var(--color-orange);';
                                      fallback.textContent = '⚠';
                                      fallback.title = 'Foto gagal dimuat (URL invalid/expired)';
                                      parent.appendChild(fallback);
                                    }
                                  }} />
                              : <div style={{
                                  width: '100%', height: '100%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 14, color: 'var(--text-mute)',
                                }}>○</div>}
                            <span style={{
                              position: 'absolute', bottom: 0, left: 0, right: 0,
                              fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                              background: filled ? 'var(--color-cyan)' : 'var(--border-dim)',
                              color: filled ? 'var(--color-bg)' : 'var(--text-mute)',
                              padding: '1px 0', textAlign: 'center', letterSpacing: 0.5,
                              opacity: filled ? 0.92 : 0.7,
                            }}>{label}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* COMPOSITE SCORING per Google Docs Section 3.2-3.5 + Biometric Gate 3.1:
                  base = bio×40 + nama×25 + handle×15 + lokasi×10 + cross×10
                  bonus = NIK +15, keywords +8/each cap16, kerja +8, lokasi +5
                  scoreCap = 100 (biometric ready) atau 60 (biometric unavailable)
                  tier = HIGH 80-100 / MED 50-79 / LOW 30-49 / DISCARDED <30 */}
              {(() => {
                const doneEntries = faceMatchStream.filter(e => e.status === 'done');
                const crossCtx = buildCrossPlatformContext(doneEntries);
                const biometricGate = checkBiometricGateFromState(faceMatchSubject);
                const hints = {
                  username:        usernameHint,
                  lokasiTerkini:   cityHint,
                  tempatKerja:     employerHint,
                  keywords:        keywords,
                };
                const scored = doneEntries.map(e => {
                  const result = computeCompositeScore(
                    {
                      nik:               subject?.nik,
                      nama_lengkap:      subject?.nama_lengkap,
                      kota_kab_ktp:      subject?.kota_kabupaten,
                      foto_url:          subject?.foto_url,
                    },
                    e,
                    hints,
                    crossCtx.getContext(e),
                    biometricGate,           // Google Docs 3.1 score cap injection
                  );
                  return { ...e, _composite: result };
                });
                // Filter DISCARDED out completely
                const visible = scored.filter(e => e._composite.tier !== 'DISCARDED');
                const high = visible.filter(e => e._composite.tier === 'HIGH').sort((a,b) => b._composite.score - a._composite.score).slice(0, 6);
                const med  = visible.filter(e => e._composite.tier === 'MEDIUM').sort((a,b) => b._composite.score - a._composite.score).slice(0, 20);
                const medOverflow = visible.filter(e => e._composite.tier === 'MEDIUM').length - med.length;
                const low  = visible.filter(e => e._composite.tier === 'LOW').sort((a,b) => b._composite.score - a._composite.score);
                const discardedCount = scored.length - visible.length;
                // Audit export builder — bundle scored entries + run metadata
                const buildAuditNow = () => buildAuditTrail({
                  subject,
                  faceMatchStream: scored,
                  platformStates,
                  hints,
                  imageSerpStats,
                  risStatus,
                  runId,
                  startedAt: runStartedAt,
                  biometricGate,
                });
                return (<>
                  {/* Audit Export toolbar — sharpening 2026-05-19: log lengkap
                      semua signals + decision path per kandidat untuk operator
                      review / forensic chain-of-custody. JSON (machine-readable)
                      + CSV summary (operator-readable). */}
                  {scored.length > 0 && (
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2 px-2 py-1.5 rounded"
                      style={{ background: 'var(--tint)', border: '1px solid var(--border-dim)' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] font-mono font-black uppercase tracking-widest" style={{ color: 'var(--color-cyan)' }}>
                          ▸ {lang === 'id' ? 'AUDIT EXPORT' : 'AUDIT EXPORT'}
                        </span>
                        <span className="text-[7px] font-mono" style={{ color: 'var(--text-mute)' }}>
                          {scored.length} {lang === 'id' ? 'kandidat · log lengkap signals + decision path' : 'candidates · full signals + decision path'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => downloadAudit(buildAuditNow(), 'json')}
                          className="text-[8px] font-mono font-black uppercase tracking-wider"
                          style={{
                            padding: '4px 10px', borderRadius: 4,
                            background: 'var(--color-cyan)', color: 'var(--color-bg)',
                            border: 'none', cursor: 'pointer',
                            letterSpacing: 1,
                          }}
                          title={lang === 'id'
                            ? 'Download audit JSON: subject, semua signals, breakdown skor, decision path per kandidat'
                            : 'Download audit JSON: subject, all signals, score breakdown, decision path per candidate'}>
                          ↓ JSON
                        </button>
                        <button type="button" onClick={() => downloadAudit(buildAuditNow(), 'csv')}
                          className="text-[8px] font-mono font-black uppercase tracking-wider"
                          style={{
                            padding: '4px 10px', borderRadius: 4,
                            background: 'transparent', color: 'var(--color-cyan)',
                            border: '1px solid var(--color-cyan)', cursor: 'pointer',
                            letterSpacing: 1,
                          }}
                          title={lang === 'id' ? 'Download CSV ringkasan untuk Excel' : 'Download CSV summary for Excel'}>
                          ↓ CSV
                        </button>
                      </div>
                    </div>
                  )}
                  {/* HIGH 80-100 — 3×2 grid 6 tile, cyan border, AUTO badge if score≥85+name match */}
              {(() => {
                return (
                  <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 10, border: '1px solid var(--border-dim)' }}>
                    <p className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--color-cyan)' }}>
                      ▸ HIGH CONFIDENCE MATCHES (80-100)
                      {/* Realtime activity indicator: scanning state saat pipeline aktif */}
                      {(() => {
                        const scanning = faceMatchStream.filter(e => e.status === 'scanning').length;
                        if (scanning === 0) return null;
                        return (
                          <span className="text-[7px] font-mono normal-case ml-2" style={{ color: 'var(--color-yellow)' }}>
                            <Loader2 size={9} className="inline animate-spin" /> {scanning} scanning…
                          </span>
                        );
                      })()}
                    </p>
                    <div className="grid grid-cols-6 gap-1.5">
                      {high.length === 0
                        ? Array.from({ length: 6 }).map((_, i) => {
                            // Realtime scan placeholder dgn cyan scan-line animation
                            // saat pipeline aktif, static dim placeholder kalau idle.
                            const isActive = ['warming','stage1','discovered','stage2','face'].includes(pipelinePhase);
                            return (
                              <div key={i} style={{
                                aspectRatio: '1', borderRadius: 4, overflow: 'hidden', position: 'relative',
                                background: 'var(--color-surface)',
                                border: `1px solid ${isActive ? 'var(--border-mid)' : 'var(--border-dim)'}`,
                                opacity: isActive ? 0.6 : 0.3,
                              }}>
                                {isActive && (
                                  <>
                                    {/* Scan line animation */}
                                    <div className="animate-pulse" style={{
                                      position: 'absolute', top: 0, left: 0, right: 0,
                                      height: 1, background: 'var(--color-cyan)',
                                      animation: `scanlineY ${1.5 + i * 0.2}s ease-in-out infinite`,
                                      animationDelay: `${i * 0.15}s`,
                                    }} />
                                    {/* Center dot pulse */}
                                    <div style={{
                                      position: 'absolute', inset: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                      <span className="animate-pulse" style={{
                                        width: 4, height: 4, borderRadius: '50%',
                                        background: 'var(--color-cyan)',
                                        animationDelay: `${i * 0.18}s`,
                                      }} />
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })
                        : high.map(e => (
                            <div key={e.id} className="animate-tile-arrive" style={{
                              position: 'relative', aspectRatio: '1', borderRadius: 4, overflow: 'hidden',
                              border: '1px solid var(--color-cyan)', cursor: 'pointer',
                            }}
                            onMouseEnter={() => setBreakdownEntry(e)}
                            onMouseLeave={() => setBreakdownEntry(null)}>
                              <img src={e.photo_url} alt={e.handle}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={handleImgFail} />
                              {/* AUTO badge per Google Docs 4.1: score≥85 + name_pts≥17.5 (double-gate) */}
                              {e._composite.auto_confirm && (
                                <span style={{
                                  position: 'absolute', top: 2, left: 2,
                                  fontSize: 5, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                                  background: 'var(--color-green)', color: 'var(--color-bg)',
                                  padding: '1px 3px', borderRadius: 2,
                                }}>AUTO</span>
                              )}
                              {/* USERNAME UNIK badge per Google Docs 4.1: alias detection */}
                              {e._composite.alias_detected && !e._composite.auto_confirm && (
                                <span style={{
                                  position: 'absolute', top: 2, left: 2,
                                  fontSize: 4, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                                  background: 'var(--color-orange)', color: 'var(--color-bg)',
                                  padding: '1px 2px', borderRadius: 2, letterSpacing: 0.2,
                                }} title="Wajah sama, nama berbeda — kemungkinan alias">ALIAS</span>
                              )}
                              {/* Data Elevated indicator per Google Docs 5.2 */}
                              {e._composite.data_elevated && (
                                <span style={{
                                  position: 'absolute', bottom: 14, left: 2,
                                  fontSize: 7, padding: '0 2px', borderRadius: 2,
                                  background: 'var(--color-purple)', color: 'var(--color-bg)',
                                }} title={`Data sensitif: ${e._composite.elevated_types.join(', ')}`}>🔒</span>
                              )}
                              <span style={{
                                position: 'absolute', top: 2, right: 2,
                                fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                                background: 'var(--color-cyan)', color: 'var(--color-bg)',
                                padding: '1px 3px', borderRadius: 2, opacity: 0.92,
                              }}>{e._composite.score}</span>
                              <span style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                fontSize: 5, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                                background: 'var(--overlay)', color: 'var(--color-cyan)',
                                padding: '1px 0', textAlign: 'center', letterSpacing: 0.3,
                              }}>{e.platform.slice(0, 4).toUpperCase()}</span>
                            </div>
                          ))}
                    </div>
                    {/* Empty-state caption — pipeline complete & 0 HIGH candidates.
                        Sebelum complete, placeholder grid + scan-line animation cukup
                        kasih sinyal "loading"; setelah complete, operator perlu konfirmasi
                        tegas bahwa memang tidak ada match HIGH. */}
                    {high.length === 0 && pipelinePhase === 'complete' && (
                      <p className="text-[8px] font-mono text-center mt-2" style={{ color: 'var(--text-mute)' }}>
                        {lang === 'id'
                          ? '— Tidak ada kandidat HIGH confidence (≥80) pada pencarian ini —'
                          : '— No HIGH confidence candidates (≥80) in this search —'}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* MEDIUM 50-79 — 12 cols × 2 rows max 20 tiles, scrollable */}
              <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 10, border: '1px solid var(--border-dim)' }}>
                <p className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--color-cyan)' }}>
                  ▸ MEDIUM CONFIDENCE (50-79) — {med.length}{medOverflow > 0 ? ` + ${medOverflow} ${lang === 'id' ? 'lainnya' : 'more'}` : ''} {lang === 'id' ? 'kandidat' : 'matches'}
                </p>
                <div className="grid grid-cols-12 gap-0.5 c-scroll" style={{ maxHeight: 110, overflowY: 'auto' }}>
                  {med.length === 0
                    ? Array.from({ length: 24 }).map((_, i) => (
                        <div key={i} style={{ aspectRatio: '1', background: 'var(--color-surface)', borderRadius: 2, opacity: 0.2 }} />
                      ))
                    : med.map(e => (
                        <div key={e.id} className="animate-tile-arrive" style={{
                          aspectRatio: '1', borderRadius: 2, overflow: 'hidden',
                          border: '1px solid var(--border-mid)', position: 'relative',
                        }}
                        title={`${e.handle} · score ${e._composite.score}/100`}>
                          <img src={e.photo_url} alt={e.handle}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={handleImgFail} />
                          <span style={{
                            position: 'absolute', bottom: 0, right: 0,
                            fontSize: 5, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                            background: 'var(--color-cyan)', color: 'var(--color-bg)',
                            padding: '0px 2px', opacity: 0.85,
                          }}>{e._composite.score}</span>
                        </div>
                      ))}
                </div>
              </div>

              {/* LOW 30-49 — collapsed list, operator klik untuk expand */}
              <div style={{ background: 'var(--color-card)', borderRadius: 8, padding: 10, border: '1px solid var(--border-dim)', flex: 1, minHeight: 50 }}>
                <button type="button"
                  onClick={() => setLowExpanded(v => !v)}
                  className="w-full text-left text-[9px] font-black uppercase tracking-widest"
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-mute)', cursor: 'pointer', padding: 0 }}>
                  {lowExpanded ? '▾' : '▸'} LOW CONFIDENCE (30-49) — {low.length} {lang === 'id' ? 'kandidat' : 'matches'} {!lowExpanded && low.length > 0 ? (lang === 'id' ? ' · klik untuk expand' : ' · click to expand') : ''}
                </button>
                {lowExpanded && (
                  <div className="grid gap-0.5 c-scroll mt-2" style={{ gridTemplateColumns: 'repeat(18, minmax(0, 1fr))', maxHeight: 130, overflowY: 'auto' }}>
                    {low.length === 0
                      ? Array.from({ length: 18 }).map((_, i) => (
                          <div key={i} style={{ aspectRatio: '1', background: 'var(--color-surface)', borderRadius: 1, opacity: 0.15 }} />
                        ))
                      : low.map(e => (
                          <div key={e.id} className="animate-tile-arrive" style={{
                            aspectRatio: '1', borderRadius: 1, overflow: 'hidden',
                            border: '1px solid var(--border-dim)', opacity: 0.7,
                          }}
                          title={`${e.handle} · score ${e._composite.score}/100`}>
                            <img src={e.photo_url} alt={e.handle}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={handleImgFail} />
                          </div>
                        ))}
                  </div>
                )}
                {discardedCount > 0 && (
                  <p className="text-[7px] font-mono mt-1" style={{ color: 'var(--text-mute)' }}>
                    ✕ {discardedCount} {lang === 'id' ? 'kandidat dibuang (skor' : 'candidates discarded (score'} &lt;30)
                  </p>
                )}
              </div>
                </>);
              })()}
            </div>
          </div>

          {/* Live counter footer */}
          <div className="flex items-center justify-between mt-4 pt-3"
            style={{ borderTop: '1px solid var(--border-dim)' }}>
            <p className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'Foto dipindai:' : 'Photos scanned:'} <span style={{ color: 'var(--color-cyan)', fontWeight: 700 }}>{liveCount}</span> · {lang === 'id' ? 'Cocok:' : 'Matched:'} <span style={{ color: 'var(--color-green)', fontWeight: 700 }}>{faceMatchStream.filter(e => e.status === 'done' && e.similarity >= 0.4).length}</span>
            </p>
            <p className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
              {pipelinePhase === 'complete'
                ? (lang === 'id' ? '✓ Discovery selesai · Review hasil sebelum lanjut' : '✓ Discovery complete · Review before proceeding')
                : (lang === 'id' ? 'Live scan aktif · Thoroughness over speed' : 'Live scan active · Thoroughness over speed')}
            </p>
          </div>

          {/* Manual advance gate — operator review hasil dulu, klik LANJUT untuk
              advance ke Stage 2 candidate selection. Tidak auto-jump. */}
          {pipelinePhase === 'complete' && state === 'awaiting_selection' && (
            <div className="mt-4 pt-3 flex items-center justify-end gap-3"
              style={{ borderTop: '1px solid var(--border-dim)' }}>
              <span className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
                {candidates.length} {lang === 'id' ? 'kandidat siap untuk seleksi' : 'candidates ready for selection'}
              </span>
              <button
                onClick={() => setTheaterDismissed(true)}
                className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all hover:opacity-90"
                style={{
                  background: 'var(--color-cyan)', color: 'var(--color-bg)',
                  fontSize: 10, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                  boxShadow: '0 0 12px var(--tint)',
                }}>
                {lang === 'id' ? 'LANJUT KE STAGE 2 →' : 'PROCEED TO STAGE 2 →'}
              </button>
            </div>
          )}

          {/* Saat pipeline complete tapi state belum sync (handleDiscover masih
              jalan parallel) — show waiting hint */}
          {pipelinePhase === 'complete' && state !== 'awaiting_selection' && state !== 'complete' && (
            <div className="mt-4 pt-3 flex items-center justify-end gap-2"
              style={{ borderTop: '1px solid var(--border-dim)' }}>
              <Loader2 size={11} className="animate-spin" color="var(--color-cyan)" />
              <span className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
                {lang === 'id' ? 'Menyiapkan kandidat untuk seleksi...' : 'Preparing candidates for selection...'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* STATE: stage_1_running (legacy) / stage_2_running — Cinematic UI vertical */}
      {((state === 'stage_1_running' && !streamMode) || state === 'stage_2_running') && (
        <div className="rounded-xl p-6 flex flex-col items-center gap-4"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>

          {/* Radar Display */}
          <div style={{ position: 'relative', width: 88, height: 88 }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              border: '1px solid rgba(0,212,255,0.25)',
            }} />
            <div style={{
              position: 'absolute', inset: 18, borderRadius: '50%',
              border: '1px solid rgba(0,212,255,0.15)',
            }} />
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 6, height: 6, borderRadius: '50%',
              background: pipelinePhase === 'complete' ? 'var(--color-green)' : 'var(--color-cyan)',
              boxShadow: '0 0 8px currentColor',
              transition: 'background 0.3s',
            }} />
            {(['warming','stage1','discovered','stage2','face'].includes(pipelinePhase) ||
              state === 'stage_1_running' || state === 'stage_2_running') && (
              <div className="animate-radar" style={{
                position: 'absolute', top: '50%', left: '50%',
                width: '50%', height: 1,
                background: 'linear-gradient(to right, rgba(0,212,255,0.85), transparent)',
                transformOrigin: 'left center',
              }} />
            )}
          </div>

          {/* Live Counter */}
          <div style={{ textAlign: 'center' }}>
            <p key={liveCount} className="animate-counter-pulse" style={{
              fontSize: 36, fontFamily: 'IBM Plex Mono', fontWeight: 900,
              color: pipelinePhase === 'complete' ? 'var(--color-green)' : 'var(--color-cyan)',
              margin: 0, letterSpacing: '-2px',
            }}>
              {liveCount || (state === 'stage_2_running' ? selectedIds.size : 0)}
            </p>
            <p style={{
              fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
              margin: 0, letterSpacing: '3px', textTransform: 'uppercase',
            }}>
              {lang === 'id' ? 'Sinyal Ditemukan' : 'Signals Found'}
            </p>
          </div>

          {/* Phase label */}
          <p className="text-[10px] font-mono uppercase tracking-widest text-center"
            style={{ color: 'var(--color-cyan)' }}>
            {state === 'stage_2_running'
              ? (lang === 'id'
                  ? `Tahap 2 — Menggali ${selectedIds.size} profil...`
                  : `Stage 2 — Extracting ${selectedIds.size} profiles...`)
              : ({
                  warming:    lang === 'id' ? 'Memanaskan KES Engine...'   : 'Warming up KES Engine...',
                  stage1:     lang === 'id' ? 'Tahap 1 — Netcomm The Radar memindai...' : 'Stage 1 — Netcomm The Radar scanning...',
                  discovered: lang === 'id' ? 'Tahap 2 — Mendalami platform...' : 'Stage 2 — Diving into platforms...',
                  stage2:     lang === 'id' ? 'Tahap 2 — Mendalami platform...' : 'Stage 2 — Diving into platforms...',
                  face:       lang === 'id' ? 'Pencocokan wajah...'        : 'Matching faces...',
                  complete:   lang === 'id' ? 'Selesai'                    : 'Complete',
                  error:      lang === 'id' ? 'Terjadi kesalahan'          : 'An error occurred',
                  idle:       lang === 'id' ? 'Tahap 1 — Netcomm The Radar memindai...' : 'Stage 1 — Netcomm The Radar scanning...',
                })[pipelinePhase] || (lang === 'id' ? 'Memindai...' : 'Scanning...')}
          </p>

          {/* Platform Tiles Grid — render skeleton immediate (riset 6.4 ghost states) */}
          {streamMode && (
            <div className="w-full max-w-2xl grid grid-cols-2 md:grid-cols-3 gap-2">
              {Array.from(selectedPlatforms).map(platform => {
                const tileState = platformStates[platform];
                const phase = tileState?.phase || 'idle';
                const tileColors = ({
                  idle:    { border: 'var(--border-dim)',                bg: 'transparent',                  text: 'var(--text-mute)' },
                  running: { border: 'var(--color-cyan)',                bg: 'rgba(0,212,255,0.05)',         text: 'var(--color-cyan)' },
                  partial: { border: 'var(--color-cyan)',                bg: 'rgba(0,212,255,0.08)',         text: 'var(--color-cyan)' },
                  done:    { border: 'var(--color-green)',               bg: 'rgba(0,255,136,0.06)',         text: 'var(--color-green)' },
                  timeout: { border: 'var(--color-yellow)',                          bg: 'rgba(255,214,10,0.06)',        text: 'var(--color-yellow)' },
                  error:   { border: 'var(--color-red)',                          bg: 'rgba(255,45,85,0.06)',         text: 'var(--color-red)' },
                  skip:    { border: 'var(--border-dim)',                bg: 'transparent',                  text: 'var(--text-mute)' },
                })[phase] || {};
                const ICONS = {
                  instagram: 'IG', tiktok: 'TT', x: 'X', facebook: 'FB',
                  youtube: 'YT', linkedin: 'LI', threads: 'TH',
                  web: 'WEB', news: 'NEWS', maps: 'MAP',
                };
                const LABELS = {
                  instagram: 'Instagram', tiktok: 'TikTok', x: 'X / Twitter',
                  facebook: 'Facebook', youtube: 'YouTube', linkedin: 'LinkedIn',
                  threads: 'Threads', web: 'Google', news: lang === 'id' ? 'Berita' : 'News', maps: 'Maps',
                };
                return (
                  <div key={platform}
                    className="animate-tile-arrive"
                    style={{
                      border: `1px solid ${tileColors.border}`,
                      background: tileColors.bg,
                      borderRadius: 8,
                      padding: '8px 10px',
                      display: 'flex', alignItems: 'center', gap: 8,
                      transition: 'all 0.4s ease',
                      opacity: phase === 'skip' ? 0.4 : 1,
                    }}>
                    <span style={{ fontSize: 14 }}>{ICONS[platform]}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                        color: tileColors.text, margin: 0, letterSpacing: 0.5 }}>
                        {LABELS[platform] || platform}
                      </p>
                      <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)', margin: 0 }}>
                        {phase === 'idle'    && (lang === 'id' ? 'Menunggu...'  : 'Waiting...')}
                        {phase === 'running' && (lang === 'id' ? 'Memindai...'  : 'Scanning...')}
                        {phase === 'partial' && `${tileState.count} ${lang === 'id' ? 'ditemukan...' : 'found...'}`}
                        {phase === 'done'    && `✓ ${tileState.count} ${lang === 'id' ? 'hasil' : 'results'}`}
                        {phase === 'timeout' && `⚠ ${tileState.count} ${lang === 'id' ? 'sebagian' : 'partial'}`}
                        {phase === 'error'   && (lang === 'id' ? '✕ Tidak tersedia' : '✕ Unavailable')}
                        {phase === 'skip'    && (lang === 'id' ? '— Tidak dipilih'  : '— Not selected')}
                      </p>
                    </div>
                    {phase === 'running' && (
                      <Loader2 size={10} className="animate-spin" color="var(--color-cyan)" />
                    )}
                    {phase === 'done' && <Check size={12} color="var(--color-green)" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Face Match Live Stream — biometric scan per kandidat di Stage 1 */}
          {streamMode && (faceMatchSubject || faceMatchStream.length > 0) && (
            <div className={`w-full max-w-3xl rounded-xl p-4 ${identityLockFlash ? 'animate-lock-burst' : ''}`}
              style={{ background: 'var(--color-card)',
                border: '1px solid rgba(0,212,255,0.4)',
                boxShadow: '0 0 20px rgba(0,212,255,0.05)',
                position: 'relative' }}>

              {/* ─── IDENTITY LOCK FLASH OVERLAY (one-shot 2.4s) ────────────
                  Fires saat subject_ready transition idle → ready.
                  Cinematic "IDENTITY LOCKED · N FACE ANGLES" reveal. */}
              {identityLockFlash && faceMatchSubject?.ready && (
                <div className="animate-identity-lock-flash"
                  style={{
                    position: 'absolute', inset: 0, borderRadius: 12,
                    background: 'radial-gradient(ellipse at center, rgba(0,212,255,0.18) 0%, rgba(0,212,255,0.04) 50%, transparent 100%)',
                    pointerEvents: 'none', zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <div style={{
                    background: 'rgba(2,6,14,0.85)',
                    border: '2px solid var(--color-cyan)',
                    borderRadius: 10,
                    padding: '14px 28px',
                    boxShadow: '0 0 32px rgba(0,212,255,0.5), inset 0 0 16px rgba(0,212,255,0.15)',
                    backdropFilter: 'blur(2px)',
                  }}>
                    <p className="text-[14px] font-black uppercase tracking-[0.3em]" style={{
                      color: 'var(--color-cyan)',
                      textShadow: '0 0 10px var(--color-cyan)',
                      margin: 0,
                    }}>
                      {lang === 'id' ? '✓ IDENTITAS TERKUNCI' : '✓ IDENTITY LOCKED'}
                    </p>
                    <p className="text-[9px] font-mono mt-1 text-center" style={{
                      color: 'var(--text-hi)', letterSpacing: 2,
                    }}>
                      {faceMatchSubject.embedding_count || (faceMatchSubject.all_photos || []).length}{' '}
                      {lang === 'id' ? 'SUDUT WAJAH · DISCOVERY DIMULAI' : 'FACE ANGLES · DISCOVERY STARTING'}
                    </p>
                  </div>
                </div>
              )}

              {/* Header — biometric system status */}
              <div className="flex items-center justify-between mb-4 pb-3"
                style={{ borderBottom: '1px solid var(--border-dim)' }}>
                <div className="flex items-center gap-3">
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: faceMatchSubject?.ready ? 'var(--color-green)' : 'var(--color-yellow)',
                    boxShadow: `0 0 8px ${faceMatchSubject?.ready ? 'var(--color-green)' : 'var(--color-yellow)'}`,
                  }} className={faceMatchSubject?.ready ? '' : 'animate-pulse'} />
                  <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'var(--color-cyan)' }}>
                    {lang === 'id' ? '▸ VERIFIKASI IDENTITAS BIOMETRIK LIVE' : '▸ LIVE BIOMETRIC IDENTITY VERIFICATION'}
                  </p>
                </div>
                <p className="text-[9px] font-mono" style={{ color: 'var(--text-mute)' }}>
                  {lang === 'id'
                    ? 'Identitas terkunci · 5 sudut multi-referensi'
                    : 'Identity locked · 5-angle multi-reference'}
                </p>
              </div>

              {/* Subject Anchor — gallery foto enrolled + scan animation */}
              {faceMatchSubject?.foto_url && (
                <div className="flex items-center gap-4 mb-4 p-3 rounded-lg"
                  style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.2)' }}>
                  {/* Photo gallery — semua foto enrolled (KTP + slots tambahan) */}
                  <div className="flex gap-1.5">
                    {(faceMatchSubject.all_photos || [faceMatchSubject.foto_url]).slice(0, 4).map((photoUrl, idx) => (
                      <div key={idx} style={{
                        position: 'relative',
                        width: idx === 0 ? 72 : 44,
                        height: idx === 0 ? 72 : 44,
                        borderRadius: 8, overflow: 'hidden',
                        border: idx === 0 ? '2px solid var(--color-cyan)' : '1px solid rgba(0,212,255,0.4)',
                        boxShadow: idx === 0 ? '0 0 12px rgba(0,212,255,0.3)' : 'none',
                      }}>
                        <img src={photoUrl} alt={`subject-${idx}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                        {/* Scan line animation pada foto utama saat extracting */}
                        {idx === 0 && !faceMatchSubject.ready && (
                          <div style={{
                            position: 'absolute', left: 0, right: 0, height: 2,
                            background: 'linear-gradient(90deg, transparent, var(--color-cyan), transparent)',
                            boxShadow: '0 0 8px var(--color-cyan)',
                            animation: 'scanline 1.2s linear infinite',
                          }} />
                        )}
                        {/* Corner brackets cuma di foto utama */}
                        {idx === 0 && [0, 1, 2, 3].map(i => (
                          <div key={i} style={{
                            position: 'absolute', width: 10, height: 10,
                            borderColor: 'var(--color-cyan)', borderStyle: 'solid', borderWidth: 0,
                            ...(i === 0 && { top: 2, left: 2,    borderTopWidth: 2,    borderLeftWidth: 2 }),
                            ...(i === 1 && { top: 2, right: 2,   borderTopWidth: 2,    borderRightWidth: 2 }),
                            ...(i === 2 && { bottom: 2, left: 2, borderBottomWidth: 2, borderLeftWidth: 2 }),
                            ...(i === 3 && { bottom: 2, right: 2, borderBottomWidth: 2, borderRightWidth: 2 }),
                          }} />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="text-[9px] font-mono uppercase tracking-wider mb-0.5" style={{ color: 'var(--color-cyan)' }}>
                      {lang === 'id' ? 'SUBJEK REFERENSI' : 'REFERENCE SUBJECT'}
                    </p>
                    <p className="text-[12px] font-black" style={{ color: 'var(--text-hi)' }}>
                      {subject?.nama_lengkap || subject?.nik}
                    </p>
                    <p className="text-[9px] font-mono mt-1" style={{ color: faceMatchSubject?.ready ? 'var(--color-green)' : 'var(--color-yellow)' }}>
                      {faceMatchSubject?.ready
                        ? `✓ ${lang === 'id' ? 'Identitas terkunci' : 'Identity locked'} · ${(faceMatchSubject.all_photos || []).length} ${lang === 'id' ? 'sudut wajah' : 'face angles'} · ${faceMatchStream.length} ${lang === 'id' ? 'kandidat dipindai' : 'candidates scanned'}`
                        : (lang === 'id' ? '› Mengunci identitas biometrik...' : '› Locking biometric identity...')}
                    </p>
                  </div>
                </div>
              )}

              {/* Live Comparison Grid — kandidat foto dengan side-by-side compare */}
              {faceMatchStream.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
                  {faceMatchStream.slice().reverse().map((entry) => {
                    const tierColor = ({
                      strong:   'var(--color-green)',
                      match:    'var(--color-cyan)',
                      possible: 'var(--color-yellow)',
                      unlikely: 'var(--text-mute)',
                      failed:   'var(--color-red)',
                    })[entry.tier] || 'var(--text-mute)';
                    const tierLabel = ({
                      strong:   lang === 'id' ? 'STRONG MATCH'   : 'STRONG MATCH',
                      match:    lang === 'id' ? 'COCOK'          : 'MATCH',
                      possible: lang === 'id' ? 'KEMUNGKINAN'    : 'POSSIBLE',
                      unlikely: lang === 'id' ? 'TIDAK COCOK'    : 'UNLIKELY',
                      failed:   lang === 'id' ? 'WAJAH TIDAK ADA': 'NO FACE DETECTED',
                    })[entry.tier] || '';
                    const isScanning = entry.status === 'scanning';
                    const isMatch = entry.tier === 'strong' || entry.tier === 'match';

                    return (
                      <div key={entry.id}
                        style={{
                          display: 'flex', gap: 8, padding: 8, borderRadius: 8,
                          background: isScanning
                            ? 'rgba(0,212,255,0.08)'
                            : isMatch ? `${tierColor}11` : 'var(--color-surface)',
                          border: `1px solid ${isScanning ? 'rgba(0,212,255,0.5)' : isMatch ? `${tierColor}66` : 'var(--border-dim)'}`,
                          transition: 'all 0.4s ease',
                          boxShadow: isMatch ? `0 0 8px ${tierColor}33` : 'none',
                        }}>

                        {/* SIDE A: Subject foto kecil (constant) */}
                        <div style={{ position: 'relative', width: 44, height: 44, borderRadius: 6, overflow: 'hidden',
                          border: '1px solid var(--color-cyan)', flexShrink: 0 }}>
                          {faceMatchSubject?.foto_url && (
                            <img src={faceMatchSubject.foto_url} alt="subj"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                        </div>

                        {/* VS arrow */}
                        <div className="flex flex-col items-center justify-center" style={{ flexShrink: 0 }}>
                          <span style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)' }}>VS</span>
                          {isScanning ? (
                            <Loader2 size={10} className="animate-spin" color="var(--color-cyan)" />
                          ) : entry.status === 'done' && entry.tier !== 'failed' ? (
                            <span style={{ fontSize: 12, color: tierColor }}>→</span>
                          ) : null}
                        </div>

                        {/* SIDE B: Candidate foto dengan scan overlay */}
                        <div style={{ position: 'relative', width: 44, height: 44, borderRadius: 6, overflow: 'hidden',
                          border: `1px solid ${tierColor}`, flexShrink: 0 }}>
                          <img src={entry.photo_url} alt={entry.handle}
                            style={{ width: '100%', height: '100%', objectFit: 'cover',
                              filter: isScanning ? 'brightness(0.7)' : 'none' }}
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = '#1a1a1a'; }} />
                          {isScanning && (
                            <div style={{
                              position: 'absolute', left: 0, right: 0, height: 2,
                              background: 'linear-gradient(90deg, transparent, var(--color-cyan), transparent)',
                              boxShadow: '0 0 6px var(--color-cyan)',
                              animation: 'scanline 1s linear infinite',
                            }} />
                          )}
                        </div>

                        {/* Result info */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <p style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                            color: 'var(--text-hi)', margin: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.platform} · @{entry.handle || '—'}
                          </p>
                          {isScanning ? (
                            <>
                              <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono',
                                color: 'var(--color-cyan)', margin: '2px 0 0' }}
                                className="animate-pulse">
                                ▶ {entry.deep_scan
                                    ? `${lang === 'id' ? 'Pencocokan mendalam' : 'Deep scan'} ${entry.scanned || 0}/${entry.total_photos}`
                                    : (lang === 'id' ? 'Mencocokkan wajah...' : 'Matching face...')}
                              </p>
                              {entry.deep_scan && entry.total_photos > 1 && (
                                <div style={{ height: 2, background: 'rgba(0,212,255,0.15)', borderRadius: 1, marginTop: 3, overflow: 'hidden' }}>
                                  <div style={{
                                    height: '100%',
                                    width: `${((entry.scanned || 0) / Math.max(1, entry.total_photos)) * 100}%`,
                                    background: 'var(--color-cyan)',
                                    transition: 'width 0.3s ease',
                                  }} />
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <p style={{ fontSize: 14, fontFamily: 'IBM Plex Mono', fontWeight: 900,
                                color: tierColor, margin: '2px 0 0', lineHeight: 1 }}>
                                {entry.tier === 'failed'
                                  ? '—'
                                  : `${(entry.similarity * 100).toFixed(0)}%`}
                              </p>
                              <p style={{ fontSize: 7, fontFamily: 'IBM Plex Mono',
                                color: tierColor, margin: '2px 0 0', fontWeight: 700, letterSpacing: 0.5 }}>
                                {tierLabel}
                                {entry.photos_matching > 0 && entry.photos_scanned > 1 && (
                                  <span style={{ color: 'var(--text-mute)', fontWeight: 400, marginLeft: 4 }}>
                                    · {entry.photos_matching}/{entry.photos_scanned} {lang === 'id' ? 'foto' : 'photos'}
                                  </span>
                                )}
                              </p>
                              {entry.disguised && (
                                <span style={{
                                  fontSize: 7, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                                  background: 'rgba(255,107,43,0.15)', color: 'var(--color-orange)',
                                  border: '1px solid rgba(255,107,43,0.40)',
                                  padding: '1px 5px', borderRadius: 3, marginTop: 2, display: 'inline-block',
                                  letterSpacing: 0.3,
                                }} title={lang === 'id' ? 'V-003: Face match ≥65% + nama akun jauh berbeda dari subject (Levenshtein <0.30) = alias/disguised account.' : 'V-003: Face match ≥65% + Levenshtein name distance <0.30 = alias/disguised account.'}>
                                  ALIAS
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty state — sebelum kandidat masuk */}
              {faceMatchStream.length === 0 && faceMatchSubject?.ready && (
                <p className="text-[9px] font-mono text-center py-3" style={{ color: 'var(--text-mute)' }}>
                  {lang === 'id'
                    ? '⏳ Menunggu kandidat dari pencarian platform...'
                    : '⏳ Waiting for candidates from platform search...'}
                </p>
              )}
            </div>
          )}

          {/* Replay indicator (admin-only badge) */}
          {streamSource === 'replay' && (
            <p className="text-[8px] font-mono px-2 py-0.5 rounded"
              style={{ color: 'var(--color-yellow)', background: 'rgba(255,214,10,0.10)', border: '1px solid rgba(255,214,10,0.3)' }}>
              {lang === 'id' ? 'Mode Demo · Replay' : 'Demo Mode · Replay'}
            </p>
          )}

          <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
            {state === 'stage_1_running' ? '~30-90 detik' : '~30-60 detik per profil (paralel)'}
          </p>
        </div>
      )}

      {/* STATE: awaiting_selection — gated supaya tidak overlap dgn theater.
          Selection muncul HANYA setelah operator klik "LANJUT KE STAGE 2"
          (theaterDismissed=true), atau saat non-streaming legacy mode. */}
      {state === 'awaiting_selection' &&
       (!streamMode || theaterDismissed) &&
       (() => {
        // § 4.4 item 15: filter weak matches < 50%, toggleable
        const visible  = showWeakMatches ? candidates : candidates.filter(c => c.confidence >= DISPLAY_THRESHOLD);
        const hidden   = candidates.length - visible.length;
        return (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--text-dim)' }}>
              {lang === 'id'
                ? `Pilih kandidat untuk diperkaya (${visible.length} ditampilkan${hidden > 0 ? ` · ${hidden} disembunyikan` : ''})`
                : `Select candidates to enrich (${visible.length} shown${hidden > 0 ? ` · ${hidden} hidden` : ''})`}
            </p>
            <div className="flex items-center gap-2">
              {hidden > 0 && (
                <button onClick={() => setShowWeakMatches(v => !v)}
                  className="text-[8px] font-mono font-bold px-2 py-1 rounded hover:opacity-80"
                  style={{ background: 'rgba(255,214,10,0.10)', color: 'var(--color-yellow)', border: '1px solid rgba(255,214,10,0.3)' }}>
                  {showWeakMatches
                    ? (lang === 'id' ? 'Sembunyikan match lemah' : 'Hide weak matches')
                    : (lang === 'id' ? `Tampilkan ${hidden} match lemah` : `Show ${hidden} weak matches`)}
                </button>
              )}
              <button onClick={selectAll}
                className="text-[8px] font-mono font-bold px-2 py-1 rounded hover:opacity-80"
                style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)' }}>
                {lang === 'id' ? 'Pilih Semua' : 'Select All'}
              </button>
              <button onClick={clearAll}
                className="text-[8px] font-mono font-bold px-2 py-1 rounded hover:opacity-80"
                style={{ background: 'var(--color-surface)', color: 'var(--text-mute)' }}>
                {lang === 'id' ? 'Hapus' : 'Clear'}
              </button>
            </div>
          </div>

          {/* Candidate cards */}
          <div className="space-y-2 max-h-96 overflow-y-auto c-scroll pr-1">
            {visible.length === 0 ? (
              <div className="rounded-xl p-5 text-center" style={{ background: 'var(--color-card)', border: '1px dashed var(--border-dim)' }}>
                <p className="text-[11px] font-bold mb-2" style={{ color: 'var(--text-hi)' }}>
                  {candidates.length === 0
                    ? (lang === 'id' ? 'Stage 1 mengembalikan 0 kandidat' : 'Stage 1 returned 0 candidates')
                    : (lang === 'id' ? `${candidates.length} kandidat ditemukan, semua di bawah threshold ${Math.round(DISPLAY_THRESHOLD*100)}%` : `${candidates.length} candidates found, all below ${Math.round(DISPLAY_THRESHOLD*100)}% threshold`)}
                </p>
                {candidates.length === 0 && (
                  <div className="text-[9px] font-mono leading-relaxed mb-3" style={{ color: 'var(--text-dim)' }}>
                    <p className="mb-1">{lang === 'id' ? 'Kemungkinan penyebab:' : 'Possible causes:'}</p>
                    <ul className="text-left max-w-md mx-auto space-y-0.5" style={{ color: 'var(--text-mute)' }}>
                      <li>• {lang === 'id' ? 'Subjek punya digital footprint terbatas' : 'Subject has limited digital footprint'}</li>
                      <li>• {lang === 'id' ? 'Username/kota/instansi tidak diisi (bantu akurasi)' : 'Username/city/employer not provided (boosts accuracy)'}</li>
                      <li>• {lang === 'id' ? 'Aktor pencarian timeout (TikTok/X paling sering)' : 'Search engine timeout (TikTok/X most common)'}</li>
                      <li>• {lang === 'id' ? 'Cek F12 console untuk detail diagnostic' : 'Check F12 console for diagnostic details'}</li>
                    </ul>
                  </div>
                )}
                {candidates.length > 0 && (
                  <button onClick={() => setShowWeakMatches(true)}
                    className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-lg hover:opacity-80"
                    style={{ background: 'rgba(255,214,10,0.15)', color: 'var(--color-yellow)', border: '1px solid rgba(255,214,10,0.4)' }}>
                    {lang === 'id' ? `Tampilkan ${candidates.length} kandidat lemah` : `Show ${candidates.length} weak candidates`}
                  </button>
                )}
                {/* Fallback ke cached findings kalau run kosong tapi cache ada */}
                {findings.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        setState('complete');
                        if (RUN_KEY) try { sessionStorage.removeItem(RUN_KEY); } catch {}
                      }}
                      className="text-[10px] font-mono font-bold px-3 py-1.5 rounded-lg hover:opacity-80"
                      style={{ background: 'rgba(0,212,255,0.15)', color: 'var(--color-cyan)', border: '1px solid rgba(0,212,255,0.4)' }}>
                      {lang === 'id'
                        ? `Lihat ${findings.length} temuan tersimpan dari run sebelumnya`
                        : `Show ${findings.length} saved findings from previous runs`}
                    </button>
                  </div>
                )}
                <p className="text-[9px] font-mono mt-3" style={{ color: 'var(--text-mute)' }}>
                  {lang === 'id' ? 'Run ID: ' : 'Run ID: '}{runId?.slice(0, 8)}...
                </p>
              </div>
            ) : (
              visible.map(c => {
                const Icon = PLATFORM_ICON[c.platform] || Globe;
                const band = confidenceBand(c.confidence, lang);
                const isSelected = selectedIds.has(c.id);
                return (
                  <div key={c.id}
                    onClick={() => toggleCandidate(c.id)}
                    className="rounded-xl p-3 cursor-pointer transition-all flex items-start gap-3"
                    style={{
                      background: isSelected ? 'rgba(0,212,255,0.08)' : 'var(--color-surface)',
                      border: `1px solid ${isSelected ? 'rgba(0,212,255,0.5)' : 'var(--border-dim)'}`,
                    }}>
                    <div className="shrink-0 mt-0.5">
                      <div className="w-4 h-4 rounded flex items-center justify-center"
                        style={{
                          background: isSelected ? 'var(--color-cyan)' : 'transparent',
                          border: `1.5px solid ${isSelected ? 'var(--color-cyan)' : 'var(--border-mid)'}`,
                        }}>
                        {isSelected && <Check size={10} color="#02060e" strokeWidth={3} />}
                      </div>
                    </div>
                    {c.thumbnail_url ? (
                      <img src={c.thumbnail_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: 'var(--color-card)' }}>
                        <Icon size={16} color="var(--text-dim)" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <Icon size={11} color="var(--text-dim)" />
                        <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
                          {PLATFORM_LABEL[c.platform] || c.platform}
                        </span>
                        <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-black"
                          style={{ background: band.color + '22', color: band.color, border: `1px solid ${band.color}55` }}>
                          {band.label} · {Math.round(c.confidence * 100)}%
                        </span>
                        {c.confidence >= 0.85 && (
                          <span className="text-[7px] font-mono px-1 py-0.5 rounded font-bold"
                            style={{ background: 'rgba(0,255,136,0.15)', color: 'var(--color-green)' }}
                            title={lang === 'id' ? 'Auto-confirmed (≥85%)' : 'Auto-confirmed (≥85%)'}>
                            ✓ AUTO
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] font-bold truncate" style={{ color: 'var(--text-hi)' }}>
                        {c.display_name || c.profile_handle || c.profile_url}
                      </p>
                      {c.profile_handle && (
                        <p className="text-[9px] font-mono" style={{ color: 'var(--text-dim)' }}>@{c.profile_handle}</p>
                      )}
                      {/* Probability breakdown — 3 sinyal terpisah supaya operator
                          bisa cek manual sebelum confirm. */}
                      {c.match_signals && (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {(() => {
                            const ms = c.match_signals;
                            const signals = [
                              { label: lang === 'id' ? 'Nama' : 'Name', value: ms.name_match, hide: ms.name_match == null },
                              { label: lang === 'id' ? 'Foto' : 'Photo', value: ms.photo_match, hide: !ms.photo_match },
                              { label: 'Username', value: ms.username_match, hide: !ms.username_match },
                            ].filter(s => !s.hide);
                            return signals.map(s => {
                              const pct = Math.round((s.value || 0) * 100);
                              const color = pct >= 80 ? 'var(--color-green)' : pct >= 60 ? 'var(--color-cyan)' : pct >= 40 ? 'var(--color-yellow)' : 'var(--text-mute)';
                              return (
                                <span key={s.label}
                                  className="text-[8px] font-mono"
                                  style={{ color: 'var(--text-mute)' }}>
                                  {s.label}: <span style={{ color, fontWeight: 700 }}>{pct}%</span>
                                </span>
                              );
                            });
                          })()}
                          {ms => null}
                        </div>
                      )}
                    </div>
                    <a href={c.profile_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      className="shrink-0 p-1 rounded hover:bg-white/5">
                      <ExternalLink size={11} color="var(--text-dim)" />
                    </a>
                  </div>
                );
              })
            )}
          </div>

          {/* Data type checkboxes */}
          {selectedIds.size > 0 && (
            <div className="rounded-xl p-3"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
              <p className="text-[9px] font-black uppercase tracking-widest font-mono mb-2"
                style={{ color: 'var(--text-dim)' }}>
                {lang === 'id' ? 'Data yang akan dikumpulkan' : 'Data types to collect'}
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'photos',          label: lang === 'id' ? 'Foto'              : 'Photos',         elevated: false },
                  { id: 'posts',           label: lang === 'id' ? 'Postingan'         : 'Posts',          elevated: false },
                  { id: 'mentions',        label: lang === 'id' ? 'Mentions publik'   : 'Public mentions',elevated: false },
                  { id: 'public_records',  label: lang === 'id' ? 'Catatan publik'    : 'Public records', elevated: false },
                  { id: 'location',        label: lang === 'id' ? 'Lokasi'            : 'Location',       elevated: false },
                  // GAP-7: Phone sebagai data type ELEVATED
                  { id: 'phone',           label: lang === 'id' ? 'Telepon (sensitif)' : 'Phone (sensitive)', elevated: true },
                ].map(dt => {
                  const active = dataTypes.has(dt.id);
                  const elColor = dt.elevated ? 'var(--color-orange)' : 'var(--color-cyan)';
                  return (
                    <button key={dt.id} onClick={() => toggleDataType(dt.id)}
                      className="flex items-center gap-1.5 text-[9px] font-mono font-bold px-2.5 py-1.5 rounded-lg transition-all"
                      style={{
                        background: active ? (dt.elevated ? 'rgba(255,107,43,0.15)' : 'rgba(0,212,255,0.15)') : 'var(--color-card)',
                        border: `1px solid ${active ? elColor : 'var(--border-dim)'}`,
                        color: active ? elColor : 'var(--text-dim)',
                      }}>
                      {active && <Check size={10} />}
                      {dt.elevated && <span title={lang === 'id' ? 'Data Kontak Sensitif' : 'Contact data — sensitive'}>⚠</span>}
                      {dt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center justify-between pt-2">
            <button onClick={handleReset}
              className="text-[9px] font-mono font-bold px-3 py-2 rounded-lg hover:opacity-80"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)', color: 'var(--text-mute)' }}>
              {lang === 'id' ? 'Batal' : 'Cancel'}
            </button>
            <div className="flex items-center gap-2">
              {/* Netcomm The Radar Native disabled — visible button per spec */}
              <button disabled
                title={lang === 'id'
                  ? 'Netcomm The Radar Native tersedia di versi mendatang — tambah monitoring kata kunci dan username berkelanjutan di sosial dan berita. Hubungi admin untuk aktivasi.'
                  : 'Netcomm The Radar Native available in future versions — adds continuous keyword + username monitoring on social + news. Contact admin to activate.'}
                className="flex items-center gap-1.5 text-[9px] font-mono font-bold px-3 py-2 rounded-lg cursor-not-allowed"
                style={{ background: 'var(--color-surface)', border: '1px dashed var(--border-dim)', color: 'var(--text-mute)', opacity: 0.6 }}>
                <Lock size={10} /> Netcomm The Radar Native (soon)
              </button>
              <button onClick={handleEnrich}
                disabled={selectedIds.size === 0 || dataTypes.size === 0}
                className="flex items-center gap-1.5 text-[10px] font-black px-4 py-2 rounded-lg transition-all"
                style={{
                  background: selectedIds.size === 0 || dataTypes.size === 0 ? 'var(--color-surface)' : 'var(--color-cyan)',
                  color: selectedIds.size === 0 || dataTypes.size === 0 ? 'var(--text-mute)' : 'var(--color-bg)',
                  cursor: selectedIds.size === 0 || dataTypes.size === 0 ? 'not-allowed' : 'pointer',
                }}>
                <Search size={11} />
                {lang === 'id' ? `Enrich (${selectedIds.size})` : `Enrich (${selectedIds.size})`}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* STATE: awaiting_filter — Decision 2: operator filter findings */}
      {state === 'awaiting_filter' && (
        <div className="flex flex-col gap-3">
          <div className="rounded-2xl p-4 flex items-start gap-3"
            style={{ background: 'rgba(255,214,10,0.08)', border: '1px solid rgba(255,214,10,0.3)' }}>
            <Flag size={18} color="#ffd60a" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: 'var(--color-yellow)' }}>
                {lang === 'id' ? 'KEPUTUSAN OPERATOR — FILTER TEMUAN' : 'OPERATOR DECISION — FILTER FINDINGS'}
              </p>
              <p className="text-[10px] mt-1 leading-relaxed" style={{ color: 'var(--text-mid)' }}>
                {lang === 'id'
                  ? `${findings.length} temuan dari Stage 2. Centang yang ingin masuk laporan ancaman akhir. Phone/lokasi sensitif (UU PDP) wajib direview operator sebelum disimpan.`
                  : `${findings.length} findings from Stage 2. Check items to include in final threat report. Sensitive phone/location (UU PDP) must be operator-reviewed before persistence.`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={filterSelectAll}
              className="text-[9px] font-mono font-bold px-3 py-1.5 rounded-lg hover:opacity-80"
              style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }}>
              {lang === 'id' ? 'Pilih Semua' : 'Select All'}
            </button>
            <button onClick={filterClearAll}
              className="text-[9px] font-mono font-bold px-3 py-1.5 rounded-lg hover:opacity-80"
              style={{ background: 'var(--color-card)', border: '1px solid var(--border-dim)', color: 'var(--text-mid)' }}>
              {lang === 'id' ? 'Bersihkan' : 'Clear'}
            </button>
            <span className="text-[9px] font-mono ml-auto" style={{ color: 'var(--text-mute)' }}>
              {filterSelections.size} / {findings.length} {lang === 'id' ? 'dipilih' : 'selected'}
            </span>
          </div>

          <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
            {findings.map(f => {
              const fid = f.id || f.url;
              const checked = filterSelections.has(fid);
              const Icon = PLATFORM_ICON[f.platform] || Globe;
              const hasElevated = f.raw_data?.is_elevated || f.raw_data?.elevated_fields?.length > 0;
              return (
                <label key={fid}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all"
                  style={{
                    background: checked ? 'rgba(0,212,255,0.06)' : 'var(--color-card)',
                    border: `1px solid ${checked ? 'rgba(0,212,255,0.3)' : 'var(--border-dim)'}`,
                  }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleFilter(fid)}
                    className="cursor-pointer" />
                  <Icon size={11} color="var(--text-dim)" className="shrink-0" />
                  <span className="text-[9px] font-mono uppercase tracking-wider shrink-0" style={{ color: 'var(--text-mute)' }}>
                    {PLATFORM_LABEL[f.platform] || f.platform}
                  </span>
                  {hasElevated && (
                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                      style={{ background: 'rgba(255,107,43,0.15)', color: 'var(--color-orange)' }}>
                      ⚠ ELEVATED
                    </span>
                  )}
                  {f.raw_data?.sentiment && (
                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                      style={{
                        background: f.raw_data.sentiment === 'negative' ? 'rgba(255,45,85,0.15)'
                                  : f.raw_data.sentiment === 'positive' ? 'rgba(0,255,136,0.15)' : 'rgba(120,120,120,0.15)',
                        color:      f.raw_data.sentiment === 'negative' ? 'var(--color-red)'
                                  : f.raw_data.sentiment === 'positive' ? 'var(--color-green)' : 'var(--text-mute)',
                      }}>
                      {f.raw_data.sentiment.toUpperCase()}
                    </span>
                  )}
                  <span className="text-[10px] truncate flex-1" style={{ color: 'var(--text-mid)' }}>
                    {(f.raw_data?.text || f.raw_data?.headline || f.raw_data?.caption || f.signal_explain || '').slice(0, 100)}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={() => setState('complete')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-bold transition-all"
              style={{ background: 'var(--color-card)', color: 'var(--text-dim)', border: '1px solid var(--border-dim)' }}>
              {lang === 'id' ? 'Lewati' : 'Skip'}
            </button>
            <button onClick={runStage3}
              disabled={filterSelections.size === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black ml-auto transition-all hover:opacity-90"
              style={{
                background: filterSelections.size === 0 ? 'var(--color-card)' : 'var(--color-cyan)',
                color: filterSelections.size === 0 ? 'var(--text-mute)' : 'var(--color-bg)',
                cursor: filterSelections.size === 0 ? 'not-allowed' : 'pointer',
                opacity: filterSelections.size === 0 ? 0.5 : 1,
              }}>
              <AlertTriangle size={11} />
              {lang === 'id' ? 'Lanjut Analisis Ancaman' : 'Continue to Threat Analysis'} →
            </button>
          </div>
        </div>
      )}

      {/* STATE: stage_3_running — Cinematic threat synthesis */}
      {state === 'stage_3_running' && (
        <div className="rounded-2xl p-8 flex flex-col items-center gap-5 text-center"
          style={{ background: 'var(--color-surface)', border: '1px solid rgba(0,212,255,0.3)',
            boxShadow: '0 0 24px rgba(0,212,255,0.1)' }}>
          <div className="relative" style={{ width: 80, height: 80 }}>
            <div className="absolute inset-0 rounded-full animate-spin"
              style={{ border: '2px solid var(--color-cyan)', borderTopColor: 'transparent', borderRightColor: 'transparent' }} />
            <div className="absolute inset-2 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,212,255,0.05)' }}>
              <span style={{ fontSize: 32 }}>🧠</span>
            </div>
          </div>
          <div>
            <p className="text-base font-black uppercase tracking-widest mb-1" style={{ color: 'var(--color-cyan)' }}>
              {lang === 'id' ? 'STAGE 3 — ANALISIS ANCAMAN' : 'STAGE 3 — THREAT ASSESSMENT'}
            </p>
            <p className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>
              {lang === 'id'
                ? 'Cross-reference findings · Reverse image · Breach check · AI threat narrative'
                : 'Cross-reference findings · Reverse image · Breach check · AI threat narrative'}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 text-[9px] font-mono w-full max-w-sm" style={{ color: 'var(--text-dim)' }}>
            <div className="flex items-center gap-2">
              <Loader2 size={10} className="animate-spin" color="var(--color-cyan)" />
              <span>{lang === 'id' ? 'Memindai database breach (HaveIBeenPwned)...' : 'Scanning breach database (HaveIBeenPwned)...'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Loader2 size={10} className="animate-spin" color="var(--color-cyan)" />
              <span>{lang === 'id' ? 'Reverse image search foto kandidat...' : 'Reverse image search candidate photos...'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Loader2 size={10} className="animate-spin" color="var(--color-cyan)" />
              <span>{lang === 'id' ? 'Hitung skor ancaman + AI narrative...' : 'Computing threat score + AI narrative...'}</span>
            </div>
          </div>
          <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
            ~30-45 detik · Qwen2.5-1.5B di Tesla T4
          </p>
        </div>
      )}

      {/* STATE: complete — Findings */}
      {state === 'complete' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[10px] font-black uppercase tracking-widest font-mono" style={{ color: 'var(--color-green)' }}>
              ✓ {lang === 'id' ? `Hasil Digital Forensic (${findings.length} temuan)` : `Digital Forensic Results (${findings.length} findings)`}
            </p>
            <div className="flex items-center gap-1.5">
              {/* G-007 Report Export — Word + PDF dengan watermark + classification + audit + quota */}
              {findings.length > 0 && (
                <>
                  <button onClick={handleExportWord}
                    className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                    style={{ background: 'rgba(0,132,184,0.12)', color: '#0084b8', border: '1px solid rgba(0,132,184,0.4)' }}
                    title={lang === 'id' ? 'Generate Word (.docx) dengan watermark + klasifikasi' : 'Generate Word (.docx) with watermark + classification'}>
                    <FileText size={10} /> WORD
                  </button>
                  <button onClick={handleExportPdf}
                    className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                    style={{ background: 'rgba(192,0,0,0.10)', color: '#c00000', border: '1px solid rgba(192,0,0,0.3)' }}
                    title={lang === 'id' ? 'Generate PDF dengan watermark + klasifikasi' : 'Generate PDF with watermark + classification'}>
                    <Printer size={10} /> PDF
                  </button>
                  <button onClick={exportJSON}
                    className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                    style={{ background: 'var(--color-card)', color: 'var(--text-mute)', border: '1px solid var(--border-dim)' }}
                    title={lang === 'id' ? 'Export JSON (admin/debug)' : 'Export JSON (admin/debug)'}>
                    <Download size={10} /> JSON
                  </button>
                </>
              )}
              {/* Iterative enrichment — enrich more candidates dari run yang sama */}
              {runId && (
                <button onClick={handleEnrichMore}
                  className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                  style={{ background: 'rgba(255,214,10,0.10)', color: 'var(--color-yellow)', border: '1px solid rgba(255,214,10,0.3)' }}
                  title={lang === 'id' ? 'Perkaya kandidat tambahan tanpa run baru' : 'Enrich more candidates without new run'}>
                  <Search size={10} /> {lang === 'id' ? 'Perkaya Lagi' : 'Enrich More'}
                </button>
              )}
              <button onClick={handleReset}
                className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)', border: '1px solid rgba(0,212,255,0.3)' }}>
                <RefreshCw size={10} /> {lang === 'id' ? 'Run Baru' : 'New Run'}
              </button>
              {/* Manual purge — admin permanent delete (UU PDP § 4.7 right-to-erasure) */}
              <button onClick={handlePurge}
                className="flex items-center gap-1 text-[9px] font-mono font-bold px-2 py-1.5 rounded-lg hover:opacity-80"
                style={{ background: 'rgba(255,45,85,0.10)', color: 'var(--color-red)', border: '1px solid rgba(255,45,85,0.3)' }}
                title={lang === 'id' ? 'Hapus permanen semua data Digital Forensic subjek ini' : 'Permanently delete all Digital Forensic data for this subject'}>
                <Trash2 size={10} /> {lang === 'id' ? 'Hapus' : 'Purge'}
              </button>
            </div>
          </div>

          {findings.length === 0 ? (
            <div className="rounded-xl p-6 text-center"
              style={{ background: 'var(--color-surface)', border: '1px dashed var(--border-dim)' }}>
              <p className="text-[10px] font-mono" style={{ color: 'var(--text-mute)' }}>
                {lang === 'id'
                  ? 'Tidak ada temuan yang tersimpan.'
                  : 'No saved findings.'}
              </p>
            </div>
          ) : (() => {
            // § 4.5: Findings grouped by source category
            const groups = {
              social_media:   findings.filter(f => f.source_type === 'social_media'),
              web:            findings.filter(f => f.source_type === 'web'),
              public_record:  findings.filter(f => f.source_type === 'public_record'),
              media_mention:  findings.filter(f => f.source_type === 'media_mention'),
            };
            const groupConfig = {
              social_media:  { label: lang === 'id' ? 'Sosial Media'    : 'Social Media',     color: 'var(--color-cyan)' },
              web:           { label: lang === 'id' ? 'Web Publik'       : 'Public Web',       color: '#7c4dff' },
              public_record: { label: lang === 'id' ? 'Catatan Publik'   : 'Public Records',   color: 'var(--color-green)' },
              media_mention: { label: lang === 'id' ? 'Media Mention'    : 'Media Mentions',   color: 'var(--color-yellow)' },
            };
            return (
              <div className="space-y-3 max-h-96 overflow-y-auto c-scroll pr-1">
                {Object.entries(groups).map(([type, items]) => {
                  if (items.length === 0) return null;
                  const cfg = groupConfig[type];
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-1 h-3 rounded-full" style={{ background: cfg.color }} />
                        <p className="text-[9px] font-black uppercase tracking-widest font-mono" style={{ color: cfg.color }}>
                          {cfg.label}
                        </p>
                        <span className="text-[8px] font-mono px-1.5 rounded" style={{ background: cfg.color + '22', color: cfg.color }}>
                          {items.length}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {items.map(f => {
                          const Icon = PLATFORM_ICON[f.platform] || Globe;
                          const band = confidenceBand(f.confidence, lang);
                          // Specific render per source_type per spec § 4.5
                          if (type === 'web' || type === 'media_mention') {
                            const r = f.raw_data || {};
                            return (
                              <div key={f.id} className="rounded-xl p-3"
                                style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[9px] font-mono font-bold truncate" style={{ color: 'var(--color-cyan)' }}>
                                      {r.publisher || (f.source_url ? new URL(f.source_url).hostname : '—')}
                                    </span>
                                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded shrink-0"
                                      style={{ background: band.color + '22', color: band.color, border: `1px solid ${band.color}55` }}>
                                      {Math.round((f.confidence || 0) * 100)}%
                                    </span>
                                    {f.is_stale && (
                                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                                        style={{ background: 'rgba(255,214,10,0.15)', color: 'var(--color-yellow)' }}>STALE</span>
                                    )}
                                    {f.raw_data?.is_elevated && (
                                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                                        title={lang === 'id' ? 'Field sensitif: ' + (f.raw_data.elevated_fields || []).join(', ') : 'Sensitive fields: ' + (f.raw_data.elevated_fields || []).join(', ')}
                                        style={{ background: 'rgba(255,45,85,0.15)', color: 'var(--color-red)', border: '1px solid rgba(255,45,85,0.4)' }}>
                                        {lang === 'id' ? 'Data Kontak Sensitif' : 'Contact data sensitive'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => handleFlagFinding(f, 'wrong_match')}
                                      title={lang === 'id' ? 'Tandai salah match' : 'Mark wrong match'}
                                      className="p-1 rounded hover:bg-white/5"><Flag size={10} color="#ff2d55" /></button>
                                    <a href={f.source_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-white/5">
                                      <ExternalLink size={10} color="var(--text-dim)" /></a>
                                  </div>
                                </div>
                                {r.headline && (
                                  <p className="text-[11px] font-bold leading-snug mb-0.5" style={{ color: 'var(--text-hi)' }}>
                                    {r.headline}
                                  </p>
                                )}
                                {r.snippet && (
                                  <p className="text-[10px] leading-relaxed mb-1" style={{ color: 'var(--text-mid)' }}>
                                    {r.snippet}
                                  </p>
                                )}
                                <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                                  {r.published_at && new Date(r.published_at).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US')}
                                  {f.adapter && ` · via ${f.adapter}`}
                                </p>
                              </div>
                            );
                          }
                          if (type === 'public_record') {
                            const r = f.raw_data || {};
                            return (
                              <div key={f.id} className="rounded-xl p-3"
                                style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[9px] font-mono uppercase tracking-wider font-bold" style={{ color: 'var(--color-green)' }}>
                                      {r.authority || 'Public Record'}
                                    </span>
                                    {r.record_type && (
                                      <span className="text-[8px] font-mono" style={{ color: 'var(--text-dim)' }}>
                                        · {r.record_type}
                                      </span>
                                    )}
                                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded shrink-0"
                                      style={{ background: band.color + '22', color: band.color, border: `1px solid ${band.color}55` }}>
                                      {Math.round((f.confidence || 0) * 100)}%
                                    </span>
                                    {f.raw_data?.is_elevated && (
                                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                                        title={lang === 'id' ? 'Field sensitif: ' + (f.raw_data.elevated_fields || []).join(', ') : 'Sensitive fields: ' + (f.raw_data.elevated_fields || []).join(', ')}
                                        style={{ background: 'rgba(255,45,85,0.15)', color: 'var(--color-red)', border: '1px solid rgba(255,45,85,0.4)' }}>
                                        {lang === 'id' ? 'Data Sensitif' : 'Sensitive'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => handleFlagFinding(f, 'wrong_match')}
                                      className="p-1 rounded hover:bg-white/5"><Flag size={10} color="#ff2d55" /></button>
                                    <a href={f.source_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-white/5">
                                      <ExternalLink size={10} color="var(--text-dim)" /></a>
                                  </div>
                                </div>
                                {r.key_data && (
                                  <p className="text-[10px] leading-relaxed mb-1" style={{ color: 'var(--text-mid)' }}>
                                    {r.key_data}
                                  </p>
                                )}
                                <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                                  {r.recorded_at && new Date(r.recorded_at).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US')}
                                </p>
                              </div>
                            );
                          }
                          // Default: social_media
                          // GAP-6: Profile photo from raw_data (multi-source fallback)
                          const profilePic = f.raw_data?.profile_pic || f.raw_data?.profile_pic_url
                                          || f.raw_data?.avatar      || f.raw_data?.profilePicUrl;
                          // GAP-4: Location origin label
                          const locInfo = locationOriginLabel(f.raw_data?.address_source, lang);
                          return (
                            <div key={f.id} className="rounded-xl p-3"
                              style={{
                                background: 'var(--color-surface)',
                                border: '1px solid var(--border-dim)',
                                opacity: f.is_saved === false ? 0.5 : 1,
                              }}>
                              <div className="flex items-start gap-3 mb-1">
                                {/* GAP-6: Profile photo thumbnail */}
                                {profilePic ? (
                                  <img src={profilePic} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0"
                                    onError={handleImgFail} />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                                    style={{ background: 'var(--color-card)' }}>
                                    <Icon size={16} color="var(--text-dim)" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                  <Icon size={11} color="var(--text-dim)" />
                                  <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-mute)' }}>
                                    {PLATFORM_LABEL[f.platform] || f.platform}
                                  </span>
                                  <span className="text-[8px] font-mono px-1.5 py-0.5 rounded shrink-0"
                                    style={{ background: band.color + '22', color: band.color, border: `1px solid ${band.color}55` }}>
                                    {band.label} · {Math.round((f.confidence || 0) * 100)}%
                                  </span>
                                  {f.is_stale && (
                                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                                      style={{ background: 'rgba(255,214,10,0.15)', color: 'var(--color-yellow)' }}>STALE</span>
                                  )}
                                  {/* AI Sentiment badge — auto-tagged dari /nlp/sentiment */}
                                  {f.raw_data?.sentiment && (() => {
                                    const sm = f.raw_data.sentiment;
                                    const sCfg = ({
                                      negative: { bg: 'rgba(255,45,85,0.15)', color: 'var(--color-red)', label: lang === 'id' ? 'NEGATIF' : 'NEGATIVE' },
                                      positive: { bg: 'rgba(0,255,136,0.15)', color: 'var(--color-green)', label: lang === 'id' ? 'POSITIF' : 'POSITIVE' },
                                      neutral:  { bg: 'rgba(120,120,120,0.15)', color: 'var(--text-mute)', label: lang === 'id' ? 'NETRAL' : 'NEUTRAL' },
                                    })[sm] || null;
                                    if (!sCfg) return null;
                                    return (
                                      <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold shrink-0"
                                        title={`AI sentiment ${Math.round((f.raw_data.sentiment_score || 0) * 100)}%`}
                                        style={{ background: sCfg.bg, color: sCfg.color }}>
                                        {sCfg.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <button onClick={() => handleFlagFinding(f, 'wrong_match')}
                                    title={lang === 'id' ? 'Tandai salah match' : 'Mark wrong match'}
                                    className="p-1 rounded hover:bg-white/5"><Flag size={10} color="#ff2d55" /></button>
                                  <a href={f.source_url} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-white/5">
                                    <ExternalLink size={10} color="var(--text-dim)" /></a>
                                </div>
                                </div>{/* close flex-1 wrapper */}
                              </div>
                              {f.signal_explain && (
                                <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-mid)' }}>
                                  {f.signal_explain}
                                </p>
                              )}
                              {/* PRD § 4.4a — Stage 4 Multi-Photo Joint Confidence phrasing */}
                              {(() => {
                                const ms = f.match_signals || {};
                                const mc = ms.photo_match_count;
                                const tc = ms.photo_total_count;
                                if (typeof tc === 'number' && tc > 0) {
                                  return (
                                    <div className="mt-1.5">
                                      <PhotoMatchExplanation
                                        matchCount={mc || 0}
                                        totalCount={tc}
                                        threshold={0.85}
                                        lang={lang}
                                        explanation={f.match_explanation_text}
                                      />
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {/* GAP-4: Location origin label */}
                              {locInfo && f.raw_data?.address && (
                                <div className="flex items-center gap-1 mt-1.5">
                                  <span className="text-[8px] font-mono px-1.5 py-0.5 rounded font-bold"
                                    style={{
                                      background: locInfo.color + '15',
                                      color: locInfo.color,
                                      border: `1px solid ${locInfo.color}40`,
                                    }}>
                                    {locInfo.elevated && '⚠ '}{locInfo.label}
                                  </span>
                                  <span className="text-[9px] font-mono" style={{ color: 'var(--text-dim)' }}>
                                    {f.raw_data.address}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center justify-between gap-2 mt-1.5">
                                <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                                  {new Date(f.retrieved_at).toLocaleString(lang === 'id' ? 'id-ID' : 'en-US')}
                                  {f.adapter && ` · via ${f.adapter}`}
                                </p>
                                {/* GAP-3: Per-finding Save toggle */}
                                <button onClick={() => handleToggleSave(f)}
                                  className="flex items-center gap-1 text-[8px] font-mono font-bold px-2 py-0.5 rounded transition-all"
                                  style={{
                                    background: f.is_saved === false ? 'var(--color-card)' : 'rgba(0,255,136,0.10)',
                                    color: f.is_saved === false ? 'var(--text-mute)' : 'var(--color-green)',
                                    border: `1px solid ${f.is_saved === false ? 'var(--border-dim)' : 'rgba(0,255,136,0.3)'}`,
                                  }}
                                  title={lang === 'id' ? 'Toggle simpan ke rekam' : 'Toggle save to record'}>
                                  {f.is_saved === false ? '○' : '●'} {lang === 'id' ? 'Simpan' : 'Save'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Stage 3 Analytics Panel — auto-show setelah Stage 3 complete (threatData populated) */}
      {state === 'complete' && findings.length > 0 && threatData && (
        <div style={{ borderTop: '1px solid var(--border-dim)', paddingTop: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            background: 'rgba(0,212,255,0.08)',
            border: '1px solid rgba(0,212,255,0.30)',
            borderRadius: 8, padding: '8px 14px',
            color: 'var(--color-cyan)',
            fontSize: 11, fontFamily: 'IBM Plex Mono', fontWeight: 700,
            letterSpacing: 1,
          }}>
            ▸ {lang === 'id' ? 'STAGE 3 — ANALISIS ANCAMAN INTELIJEN' : 'STAGE 3 — THREAT INTELLIGENCE ANALYSIS'}
            <span style={{ marginLeft: 'auto', fontSize: 8, opacity: 0.7 }}>
              {threatData.category?.toUpperCase()} · {threatData.threat_score}/100
            </span>
          </div>

          {stage3Error && (
            <p className="text-[10px] font-mono mb-2 px-3 py-2 rounded" style={{
              color: 'var(--color-red)', background: 'rgba(255,45,85,0.06)', border: '1px solid rgba(255,45,85,0.2)',
            }}>
              ⚠ Stage 3 partial fail: {stage3Error}
            </p>
          )}

          {true && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
              {/* Kiri: Network graph */}
              <NetworkGraph
                subjectName={subject?.nama_lengkap}
                edges={networkEdges.length > 0 ? networkEdges : findings.slice(0,20).map(f => ({
                  source_handle: subject?.nik || 'subject',
                  target_handle: f.raw_data?.username || f.platform,
                  edge_type: 'owns_account',
                  platform: f.platform,
                  weight: f.confidence || 0.5,
                }))}
                lang={lang}
              />

              {/* Kanan: Threat gauge + breach */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: 14,
                  border: '1px solid var(--border-dim)',
                }}>
                  <p style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
                               textTransform: 'uppercase', letterSpacing: 3, marginBottom: 10 }}>
                    {lang === 'id' ? 'Skor Ancaman' : 'Threat Score'}
                  </p>
                  <ThreatScoreGauge
                    score={threatData?.threat_score ?? 0}
                    factors={threatData?.factors || []}
                    lang={lang}
                    animated={true}
                  />
                  {!threatData && (
                    <p style={{ fontSize: 9, color: 'var(--text-mute)', marginTop: 8, textAlign: 'center' }}>
                      {lang === 'id'
                        ? 'Jalankan Digital Forensic untuk menghitung skor'
                        : 'Run Digital Forensic to compute score'}
                    </p>
                  )}
                </div>

                <div style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: 14,
                  border: '1px solid var(--border-dim)',
                }}>
                  <p style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
                               textTransform: 'uppercase', letterSpacing: 3, marginBottom: 10 }}>
                    {lang === 'id' ? 'Breach Database' : 'Breach Database'}
                  </p>
                  <BreachCheckBadge breaches={breachData} lang={lang} />
                </div>
              </div>

              {/* AI-Generated Threat Narrative — dari Qwen2.5-1.5B di T4 GPU */}
              {threatData?.narrative && (
                <div style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: 16,
                  border: '1px solid rgba(0,212,255,0.3)', marginTop: 12,
                  boxShadow: '0 0 12px rgba(0,212,255,0.05)',
                }}>
                  <div className="flex items-center gap-2 mb-3 pb-2"
                    style={{ borderBottom: '1px solid var(--border-dim)' }}>
                    <span style={{ fontSize: 10 }}>🧠</span>
                    <p style={{ fontSize: 9, fontFamily: 'IBM Plex Mono', color: 'var(--color-cyan)',
                                 textTransform: 'uppercase', letterSpacing: 3, fontWeight: 700 }}>
                      {lang === 'id' ? 'Analisis Ancaman AI' : 'AI Threat Analysis'}
                    </p>
                    <span style={{ fontSize: 8, fontFamily: 'IBM Plex Mono',
                      color: 'var(--text-mute)', marginLeft: 'auto' }}>
                      AI Engine
                    </span>
                  </div>
                  <div style={{ fontSize: 11, lineHeight: 1.6, color: 'var(--text-mid)',
                    whiteSpace: 'pre-wrap', fontFamily: 'IBM Plex Sans' }}>
                    {threatData.narrative}
                  </div>
                  <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
                    marginTop: 10, fontStyle: 'italic' }}>
                    ⚠ {lang === 'id'
                      ? 'Narasi di-generate AI berdasar data faktor. Bukan opini analyst manusia.'
                      : 'Narrative AI-generated from factor data. Not a human analyst opinion.'}
                  </p>
                </div>
              )}

              {/* B-002 — Forensic Analysis Report Subsystem */}
              <div style={{
                background: 'var(--color-card)', borderRadius: 10, padding: 14,
                border: '1px solid rgba(124,77,255,0.4)', marginTop: 12,
                boxShadow: '0 0 12px rgba(124,77,255,0.05)',
              }}>
                <div className="flex items-center gap-2 mb-3 pb-2"
                  style={{ borderBottom: '1px solid var(--border-dim)' }}>
                  <FileText size={12} color="#7c4dff" />
                  <p style={{ fontSize: 10, fontFamily: 'IBM Plex Mono', color: '#7c4dff',
                               textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}>
                    {lang === 'id' ? 'LAPORAN ANALISIS FORENSIK' : 'FORENSIC ANALYSIS REPORT'}
                  </p>
                </div>
                {!forensicResult ? (
                  <>
                    <p className="text-[10px] mb-3" style={{ color: 'var(--text-mid)', lineHeight: 1.5 }}>
                      {lang === 'id'
                        ? 'Generate dokumen Word + PDF profesional dari semua temuan. Termasuk daftar pustaka, catatan kaki sumber, watermark klasifikasi, audit trail.'
                        : 'Generate professional Word + PDF document from all findings. Includes bibliography, footnotes, classification watermark, audit trail.'}
                    </p>
                    {/* T03 — AI Engine guard. Banner reflect actual hfStatus (prop dari App.jsx
                        via NeoIntel → OsintTab). Kalau bukan 'online', disable button + show
                        retry. Cegah klik 'Run Forensic' pas HF cold start → timeout crash demo. */}
                    {appHfStatus !== 'online' && (
                      <div className="rounded-lg p-2.5 mb-3 flex items-start gap-2" style={{
                        background: 'rgba(255,214,10,0.08)', border: '1px solid rgba(255,214,10,0.35)',
                      }}>
                        <AlertTriangle size={12} color="var(--color-yellow)" className="shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-[10px] font-bold" style={{ color: 'var(--color-yellow)' }}>
                            {lang === 'id'
                              ? (appHfStatus === 'waking' ? '⏳ AI Engine sedang pemanasan...' : '⚠ AI Engine Offline')
                              : (appHfStatus === 'waking' ? '⏳ AI Engine warming up...' : '⚠ AI Engine Offline')}
                          </p>
                          <p className="text-[9px] mt-1" style={{ color: 'var(--text-mid)' }}>
                            {lang === 'id'
                              ? 'Tunggu AI Engine aktif sebelum generate laporan forensik (cegah timeout cold-start).'
                              : 'Wait for AI Engine to be active before generating forensic report (prevent cold-start timeout).'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => pingHFSpace(30000).catch(() => {})}
                          className="text-[9px] font-bold px-2.5 py-1 rounded hover:opacity-80"
                          style={{
                            background: 'rgba(255,214,10,0.15)', color: 'var(--color-yellow)',
                            border: '1px solid rgba(255,214,10,0.4)', flexShrink: 0,
                          }}
                        >
                          {lang === 'id' ? 'Coba Lagi' : 'Retry'}
                        </button>
                      </div>
                    )}
                    <button onClick={() => setShowForensicModal(true)}
                      disabled={forensicGenerating || appHfStatus !== 'online'}
                      title={appHfStatus !== 'online'
                        ? (lang === 'id' ? 'Tunggu AI Engine aktif' : 'Wait for AI Engine to be online')
                        : ''}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider hover:opacity-90 transition-all"
                      style={{
                        background: (forensicGenerating || appHfStatus !== 'online') ? 'var(--color-card)' : 'rgba(124,77,255,0.15)',
                        color: (forensicGenerating || appHfStatus !== 'online') ? 'var(--text-mute)' : '#7c4dff',
                        border: '1px solid rgba(124,77,255,0.5)',
                        cursor: forensicGenerating ? 'wait' : (appHfStatus !== 'online' ? 'not-allowed' : 'pointer'),
                        opacity: (appHfStatus !== 'online' && !forensicGenerating) ? 0.6 : 1,
                      }}>
                      {forensicGenerating
                        ? <><Loader2 size={11} className="animate-spin" /> {lang === 'id' ? 'Membuat laporan...' : 'Generating...'}</>
                        : <><FileText size={11} /> {lang === 'id' ? 'Buat Laporan Forensik' : 'Generate Forensic Report'}</>}
                    </button>
                    {forensicError && (
                      <p className="text-[9px] mt-2 px-2 py-1 rounded" style={{
                        color: 'var(--color-red)', background: 'rgba(255,45,85,0.06)',
                        border: '1px solid rgba(255,45,85,0.2)',
                      }}>
                        ⚠ {forensicError}
                      </p>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="text-[10px] mb-3" style={{ color: 'var(--color-green)' }}>
                      ✓ {lang === 'id' ? 'Laporan berhasil di-generate' : 'Report generated'} ·
                      {' '}{forensicResult.findings_count} {lang === 'id' ? 'temuan' : 'findings'} ·
                      {' '}{forensicResult.bibliography_count} {lang === 'id' ? 'sumber' : 'sources'}
                    </p>
                    <div className="flex items-center gap-2">
                      {forensicResult.docx_url && (
                        <a href={forensicResult.docx_url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold hover:opacity-80"
                          style={{ background: 'rgba(0,212,255,0.10)', color: 'var(--color-cyan)', border: '1px solid rgba(0,212,255,0.3)' }}>
                          <Download size={10} /> Word ({Math.round((forensicResult.docx_size || 0) / 1024)} KB)
                        </a>
                      )}
                      {forensicResult.pdf_url && (
                        <a href={forensicResult.pdf_url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold hover:opacity-80"
                          style={{ background: 'rgba(255,45,85,0.10)', color: 'var(--color-red)', border: '1px solid rgba(255,45,85,0.3)' }}>
                          <Download size={10} /> PDF ({Math.round((forensicResult.pdf_size || 0) / 1024)} KB)
                        </a>
                      )}
                      <button onClick={() => { setForensicResult(null); setShowForensicModal(true); }}
                        className="text-[9px] font-mono ml-auto px-2 py-1 rounded hover:opacity-80"
                        style={{ color: 'var(--text-dim)', border: '1px solid var(--border-dim)' }}>
                        {lang === 'id' ? 'Generate ulang' : 'Re-generate'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* B-002 — Forensic Report Config Modal */}
          {showForensicModal && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }} onClick={() => setShowForensicModal(false)}>
              <div onClick={e => e.stopPropagation()} style={{
                background: 'var(--color-bg)', border: '1px solid rgba(124,77,255,0.5)',
                borderRadius: 12, padding: 24, maxWidth: 480, width: '100%',
                boxShadow: '0 0 40px rgba(124,77,255,0.2)',
              }}>
                <div className="flex items-center gap-2 mb-4 pb-3"
                  style={{ borderBottom: '1px solid var(--border-dim)' }}>
                  <FileText size={16} color="#7c4dff" />
                  <p className="text-[12px] font-black uppercase tracking-widest" style={{ color: '#7c4dff' }}>
                    {lang === 'id' ? 'KONFIGURASI LAPORAN FORENSIK' : 'FORENSIC REPORT CONFIG'}
                  </p>
                </div>

                {/* Report Type */}
                <div className="mb-4">
                  <p className="text-[9px] font-black uppercase tracking-wider mb-2" style={{ color: 'var(--text-mute)' }}>
                    {lang === 'id' ? 'Tipe Laporan' : 'Report Type'}
                  </p>
                  <div className="flex gap-2">
                    {[
                      { id: 'factual',    label: lang === 'id' ? 'Faktual' : 'Factual',    desc: lang === 'id' ? 'Fakta saja, tanpa narasi AI' : 'Evidence only, no AI narrative' },
                      { id: 'analytical', label: lang === 'id' ? 'Analitik Intelijen' : 'Analytical Intelligence', desc: lang === 'id' ? 'Narasi AI + kesimpulan' : 'AI narrative + conclusion' },
                    ].map(t => (
                      <button key={t.id} onClick={() => setForensicReportType(t.id)}
                        className="flex-1 text-left p-3 rounded-lg transition-all"
                        style={{
                          background: forensicReportType === t.id ? 'rgba(124,77,255,0.10)' : 'var(--color-card)',
                          border: `1px solid ${forensicReportType === t.id ? '#7c4dff' : 'var(--border-dim)'}`,
                          color: forensicReportType === t.id ? '#7c4dff' : 'var(--text-mid)',
                        }}>
                        <p className="text-[10px] font-bold mb-1">{t.label}</p>
                        <p className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Classification */}
                <div className="mb-4">
                  <p className="text-[9px] font-black uppercase tracking-wider mb-2" style={{ color: 'var(--text-mute)' }}>
                    {lang === 'id' ? 'Klasifikasi' : 'Classification'}
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { id: 'PUBLIC',       color: 'var(--color-green)' },
                      { id: 'INTERNAL',     color: 'var(--color-cyan)' },
                      { id: 'CONFIDENTIAL', color: 'var(--color-orange)' },
                      { id: 'SECRET',       color: 'var(--color-red)' },
                    ].map(c => (
                      <button key={c.id} onClick={() => setForensicClassification(c.id)}
                        className="text-[8px] font-black uppercase tracking-wider py-2 rounded transition-all"
                        style={{
                          background: forensicClassification === c.id ? `${c.color}22` : 'var(--color-card)',
                          border: `1px solid ${forensicClassification === c.id ? c.color : 'var(--border-dim)'}`,
                          color: forensicClassification === c.id ? c.color : 'var(--text-mute)',
                        }}>
                        {c.id}
                      </button>
                    ))}
                  </div>
                </div>

                {/* B-002 acceptance #3: Photo picker grid — operator selects
                    photos untuk dilampirkan ke laporan. Aggregate sources:
                    1) Subject enrolled photos (5-pose dari faceMatchSubject)
                    2) Face match candidate photos (similarity ≥0.5) */}
                {(() => {
                  const photoCandidates = [];
                  // Subject 5-pose photos. Same bug fix as above — empty array truthy.
                  const subjectPhotos = [
                    ...((Array.isArray(faceMatchSubject?.all_photos) && faceMatchSubject.all_photos.length > 0)
                      ? faceMatchSubject.all_photos
                      : []),
                    faceMatchSubject?.foto_url,
                    subject?.foto_url,
                  ].filter(Boolean);
                  const subjectPhotosUnique = [...new Set(subjectPhotos)];
                  subjectPhotosUnique.forEach((url, i) => {
                    photoCandidates.push({
                      url,
                      label: POSE_LABELS[i] || `FOTO ${i+1}`,
                      source: 'subject',
                      sourceLabel: lang === 'id' ? 'Subject' : 'Subject',
                    });
                  });
                  // Face match candidate photos (top similarity)
                  faceMatchStream
                    .filter(e => e.status === 'done' && e.similarity >= 0.4 && e.photo_url)
                    .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
                    .slice(0, 8)
                    .forEach(e => {
                      if (!photoCandidates.find(p => p.url === e.photo_url)) {
                        photoCandidates.push({
                          url: e.photo_url,
                          label: `${(SOURCE_BRAND_LABEL[e.platform] || e.platform || '').toString().slice(0, 8)} ${Math.round(e.similarity * 100)}%`,
                          source: 'match',
                          sourceLabel: e.platform || 'match',
                        });
                      }
                    });

                  if (photoCandidates.length === 0) return null;

                  // Auto-select all by default kalau set masih kosong
                  if (forensicSelectedPhotos.size === 0 && photoCandidates.length > 0) {
                    // Auto-select first 12 (cap)
                    const auto = new Set(photoCandidates.slice(0, 12).map(p => p.url));
                    setTimeout(() => setForensicSelectedPhotos(auto), 0);
                  }

                  const togglePhoto = (url) => {
                    setForensicSelectedPhotos(prev => {
                      const next = new Set(prev);
                      if (next.has(url)) next.delete(url);
                      else if (next.size < 12) next.add(url);  // hard cap 12
                      return next;
                    });
                  };

                  return (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: 'var(--text-dim)' }}>
                          ▸ {lang === 'id' ? 'Pilih Foto untuk Laporan' : 'Select Photos for Report'}
                        </p>
                        <span className="text-[8px] font-mono" style={{ color: 'var(--text-mute)' }}>
                          {forensicSelectedPhotos.size}/{Math.min(photoCandidates.length, 12)} {lang === 'id' ? 'dipilih (max 12)' : 'selected (max 12)'}
                        </span>
                      </div>
                      <div className="grid grid-cols-6 gap-1.5 max-h-48 overflow-y-auto pr-1">
                        {photoCandidates.map((p, idx) => {
                          const selected = forensicSelectedPhotos.has(p.url);
                          return (
                            <button key={idx} type="button" onClick={() => togglePhoto(p.url)}
                              className="relative rounded overflow-hidden transition-all hover:opacity-90"
                              style={{
                                aspectRatio: '1',
                                border: `2px solid ${selected ? 'var(--color-cyan)' : 'var(--border-dim)'}`,
                                opacity: selected ? 1 : 0.55,
                                cursor: 'pointer',
                              }}
                              title={p.sourceLabel}>
                              <img src={p.url} alt={p.label}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={handleImgFail} />
                              {selected && (
                                <span style={{
                                  position: 'absolute', top: 2, right: 2,
                                  width: 14, height: 14, borderRadius: '50%',
                                  background: 'var(--color-cyan)', color: 'var(--color-bg)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 10, fontWeight: 900,
                                }}>✓</span>
                              )}
                              <span style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                fontSize: 6, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                                background: 'var(--overlay)', color: selected ? 'var(--color-cyan)' : 'var(--text-mute)',
                                padding: '1px 0', textAlign: 'center', letterSpacing: 0.3,
                              }}>{p.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Info */}
                <p className="text-[9px] font-mono mb-4 px-2 py-2 rounded" style={{
                  background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)',
                  color: 'var(--text-mid)', lineHeight: 1.5,
                }}>
                  {lang === 'id'
                    ? `${findings.length} temuan + ${(threatData?.factors?.length || 0)} faktor + ${breachData.length} breach + ${forensicSelectedPhotos.size} foto akan masuk laporan. Word + PDF di-generate paralel.`
                    : `${findings.length} findings + ${(threatData?.factors?.length || 0)} factors + ${breachData.length} breaches + ${forensicSelectedPhotos.size} photos will be included. Word + PDF generated in parallel.`}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3" style={{ borderTop: '1px solid var(--border-dim)' }}>
                  <button onClick={() => setShowForensicModal(false)}
                    disabled={forensicGenerating}
                    className="px-4 py-2 rounded-lg text-[10px] font-bold"
                    style={{ background: 'var(--color-card)', color: 'var(--text-dim)', border: '1px solid var(--border-dim)' }}>
                    {lang === 'id' ? 'Batal' : 'Cancel'}
                  </button>
                  <button onClick={generateForensicReport}
                    disabled={forensicGenerating}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider ml-auto hover:opacity-90"
                    style={{
                      background: forensicGenerating ? 'var(--color-card)' : '#7c4dff',
                      color: forensicGenerating ? 'var(--text-mute)' : '#ffffff',
                      cursor: forensicGenerating ? 'wait' : 'pointer',
                    }}>
                    {forensicGenerating
                      ? <><Loader2 size={11} className="animate-spin" /> {lang === 'id' ? 'Membuat...' : 'Generating...'}</>
                      : <><FileText size={11} /> {lang === 'id' ? 'Buat Laporan' : 'Generate Report'}</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STATE: failed */}
      {state === 'failed' && (
        <div className="rounded-xl p-6 flex flex-col items-center gap-3 text-center"
          style={{ background: 'rgba(255,45,85,0.06)', border: '1px solid rgba(255,45,85,0.3)' }}>
          <AlertTriangle size={28} color="#ff2d55" />
          <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--color-red)' }}>
            {lang === 'id' ? 'Digital Forensic Gagal' : 'Digital Forensic Failed'}
          </p>
          <p className="text-[10px] font-mono leading-relaxed max-w-md" style={{ color: 'var(--text-dim)' }}>
            {translateError(error, lang)}
          </p>
          <button onClick={handleReset}
            className="flex items-center gap-1.5 text-[10px] font-bold px-4 py-2 rounded-lg hover:opacity-80"
            style={{ background: 'var(--color-cyan)', color: 'var(--color-bg)' }}>
            <RefreshCw size={11} /> {lang === 'id' ? 'Coba Lagi' : 'Retry'}
          </button>
        </div>
      )}

      {/* Legal disclaimer footer — selalu tampil */}
      <div className="rounded-lg px-3 py-2 mt-2"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--border-lo)' }}>
        <p className="text-[8px] font-mono leading-relaxed" style={{ color: 'var(--text-mute)' }}>
          § {lang === 'id'
            ? 'Hasil Digital Forensic BUKAN identitas terkonfirmasi. Confidence score = indikator probabilistik. Retention default 24 bulan, audit log append-only. Compliance UU PDP.'
            : 'Digital Forensic results are NOT confirmed identity. Confidence = probabilistic indicator. Default retention 24 months, append-only audit log. UU PDP compliant.'}
        </p>
      </div>
    </div>
  );
};

export default OsintTab;
